/**
 * Shape Builder
 *
 * Creates shapes from line intersections and boundaries
 */

import * as THREE from 'three';
import { DetectedLine, LineShape, LinePrompt, ExternalConnection } from './types';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

/** A connection between two prompt sides at an intersection point */
interface PromptSideConnection {
  promptA: number;
  promptB: number;
  sideA: 'pos' | 'neg';
  sideB: 'pos' | 'neg';
  point: THREE.Vector3;
}

/** Data about the intersection between two prompt lines */
interface PromptIntersectionData {
  point: THREE.Vector3;
  distFromI: number;
  distFromJ: number;
  sideI: 'pos' | 'neg';
  sideJ: 'pos' | 'neg';
}

export class ShapeBuilder {
  private nextShapeId = 0;

  /**
   * Optional. When set + a `BasePlaneFrame` is installed on the
   * transformer, `createShapeFromPrompts` / `createShapeFromSelectedLines`
   * / `createShapeWithExternalPolygon` / `buildShapeFromLine` run all
   * their 2D intersection math in the plane's local (u, v) frame:
   *
   *   1. On entry, prompt `worldPoint` / `direction` are mutated to
   *      hold local-frame values; detected-line endpoints are cloned
   *      to local; external-polygon points are cloned to local.
   *   2. The existing XY math runs unchanged — `posConn.point.z =
   *      prompt.worldPoint.z` now collapses to `local z = 0`, which is
   *      on-plane.
   *   3. On exit, the prompts' worldPoint/direction are restored from
   *      cached world values; output vertices, centers, and
   *      newly-written `connections.{positive,negative}.point` get
   *      mapped from local back to world.
   *
   * When no frame is installed (e.g. surface tools / auto-room callers)
   * the transformers are identity round-trips and behavior is byte-
   * identical to the pre-frame ShapeBuilder.
   */
  private coordinateTransformer: CoordinateTransformer | null = null;

  setCoordinateTransformer(transformer: CoordinateTransformer | null): void {
    this.coordinateTransformer = transformer;
  }

  /**
   * Run `fn` with prompts temporarily mutated into the base-plane local
   * frame, then map the returned LineShape's vertices/center back to
   * world and restore the prompts' world-frame fields. Any `connections.
   * {positive,negative}.point` written into prompts during `fn` (e.g. by
   * `updatePromptConnections`) is also mapped to world.
   *
   * No-frame path: identity behavior, no allocations beyond a single
   * `getBasePlaneFrame()` check.
   */
  private withLocalFrame<T extends LineShape | null>(prompts: LinePrompt[], fn: () => T): T {
    const t = this.coordinateTransformer;
    if (!t || !t.getBasePlaneFrame()) return fn();

    // Snapshot world coords so we can restore them after `fn`.
    const snapshots = prompts.map((p) => ({
      worldPoint: p.worldPoint.clone(),
      direction: p.direction.clone(),
    }));

    // Mutate to local for the inner algorithm.
    for (const p of prompts) {
      p.worldPoint.copy(t.toBasePlaneLocal(p.worldPoint));
      p.direction.copy(t.toBasePlaneLocalDir(p.direction));
    }

    const result = fn();

    // Map any connection points the inner algorithm wrote into the
    // prompts (local → world). `updatePromptConnections` writes the
    // SAME Vector3 reference into both `prompt[A].connections.{...}.point`
    // and `prompt[B].connections.{...}.point` for each intersection
    // pair (see ShapeBuilder line ~3096–3131), so a naive iteration
    // would double-map shared points and send them to (world - origin*2)
    // far off the canvas. Track visited point instances to map each
    // exactly once.
    const visitedPoints = new Set<THREE.Vector3>();
    for (const p of prompts) {
      const pos = p.connections?.positive;
      const neg = p.connections?.negative;
      if (pos?.point && !visitedPoints.has(pos.point)) {
        visitedPoints.add(pos.point);
        pos.point.copy(t.fromBasePlaneLocal(pos.point));
      }
      if (neg?.point && !visitedPoints.has(neg.point)) {
        visitedPoints.add(neg.point);
        neg.point.copy(t.fromBasePlaneLocal(neg.point));
      }
    }

    // Restore prompt world-frame fields.
    for (let i = 0; i < prompts.length; i++) {
      prompts[i].worldPoint.copy(snapshots[i].worldPoint);
      prompts[i].direction.copy(snapshots[i].direction);
    }

    // Map the returned shape's vertices + center to world.
    if (result) {
      for (const v of result.vertices) v.copy(t.fromBasePlaneLocal(v));
      if (result.center) result.center.copy(t.fromBasePlaneLocal(result.center));
    }

    return result;
  }

  /**
   * Build a shape from a selected line by finding intersecting lines
   */
  buildShapeFromLine(selectedLine: DetectedLine, allLines: DetectedLine[]): LineShape | null {
    // Find all lines that intersect with the selected line or are parallel
    const connectedLines = this.findConnectedLines(selectedLine, allLines);

    if (connectedLines.length < 2) {
      // Not enough lines to form a shape
      return null;
    }

    // Find intersection points and create polygon
    const vertices = this.findPolygonVertices(selectedLine, connectedLines);

    if (vertices.length < 3) {
      return null;
    }

    // Check if shape is closed
    const isClosed = this.isShapeClosed(vertices);

    // Calculate center
    const center = this.calculateCenter(vertices);

    return {
      id: `shape_${this.nextShapeId++}`,
      vertices,
      boundaryLines: [selectedLine, ...connectedLines],
      isClosed,
      center,
    };
  }

  /**
   * Find lines that connect to or intersect with the given line
   */
  private findConnectedLines(line: DetectedLine, allLines: DetectedLine[]): DetectedLine[] {
    const connected: DetectedLine[] = [];
    const threshold = 0.5; // meters - distance threshold for connection

    for (const otherLine of allLines) {
      if (otherLine.id === line.id) continue;

      // Check if lines intersect or endpoints are close
      if (this.linesIntersect(line, otherLine) || this.endpointsClose(line, otherLine, threshold)) {
        connected.push(otherLine);
      }
    }

    return connected;
  }

  /**
   * Check if two lines intersect
   */
  private linesIntersect(line1: DetectedLine, line2: DetectedLine): boolean {
    const intersection = this.findIntersection(line1.start, line1.end, line2.start, line2.end);
    return intersection !== null;
  }

  /**
   * Find intersection point between two line segments
   * If lines don't actually intersect, finds the closest point between infinite lines
   */
  private findIntersection(p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, p4: THREE.Vector3): THREE.Vector3 | null {
    // Project to 2D (use X-Y plane)
    const x1 = p1.x,
      y1 = p1.y;
    const x2 = p2.x,
      y2 = p2.y;
    const x3 = p3.x,
      y3 = p3.y;
    const x4 = p4.x,
      y4 = p4.y;

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

    if (Math.abs(denom) < 0.0001) {
      return null; // Parallel or coincident
    }

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    // Calculate intersection point on infinite lines
    const x = x1 + t * (x2 - x1);
    const y = y1 + t * (y2 - y1);
    const z = p1.z + t * (p2.z - p1.z); // Interpolate Z
    const intersection = new THREE.Vector3(x, y, z);

    // Check if intersection is within both line segments
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return intersection;
    }

    // Lines don't actually intersect - check if intersection is reasonably close
    // Find closest point on each segment to the intersection
    const closestOnLine1 = this.closestPointOnSegment(p1, p2, intersection);
    const closestOnLine2 = this.closestPointOnSegment(p3, p4, intersection);

    // If the distance between closest points is reasonable, use their midpoint
    const distance = closestOnLine1.distanceTo(closestOnLine2);
    const maxDistance = 1.0; // 1 meter threshold

    if (distance < maxDistance) {
      // Use midpoint between the two closest points
      return new THREE.Vector3().addVectors(closestOnLine1, closestOnLine2).multiplyScalar(0.5);
    }

    return null;
  }

  /**
   * Find closest point on a line segment to a given point
   */
  private closestPointOnSegment(lineStart: THREE.Vector3, lineEnd: THREE.Vector3, point: THREE.Vector3): THREE.Vector3 {
    const lineDir = new THREE.Vector3().subVectors(lineEnd, lineStart);
    const lineLength = lineDir.length();

    if (lineLength < 0.0001) {
      return lineStart.clone();
    }

    lineDir.normalize();

    const toPoint = new THREE.Vector3().subVectors(point, lineStart);
    const t = toPoint.dot(lineDir);

    // Clamp to segment bounds
    const clampedT = Math.max(0, Math.min(lineLength, t));

    return new THREE.Vector3().copy(lineStart).add(lineDir.multiplyScalar(clampedT));
  }

  /**
   * Check if endpoints of two lines are close
   */
  private endpointsClose(line1: DetectedLine, line2: DetectedLine, threshold: number): boolean {
    const pairs = [
      [line1.start, line2.start],
      [line1.start, line2.end],
      [line1.end, line2.start],
      [line1.end, line2.end],
    ];

    for (const [p1, p2] of pairs) {
      if (p1.distanceTo(p2) < threshold) {
        return true;
      }
    }

    return false;
  }

  /**
   * Find polygon vertices from line intersections
   * Prioritizes intersection points, falls back to endpoints only when needed
   */
  private findPolygonVertices(startLine: DetectedLine, connectedLines: DetectedLine[]): THREE.Vector3[] {
    const vertices: THREE.Vector3[] = [];
    const allLines = [startLine, ...connectedLines];

    // Find all intersection points between lines
    for (let i = 0; i < allLines.length; i++) {
      for (let j = i + 1; j < allLines.length; j++) {
        const intersection = this.findIntersection(allLines[i].start, allLines[i].end, allLines[j].start, allLines[j].end);

        if (intersection) {
          vertices.push(intersection);
        }
      }
    }

    // Only use endpoints as fallback if we have very few intersections
    if (vertices.length < 3) {
      // Not enough intersection points, add line endpoints that are not near any intersection
      const endpointThreshold = 0.3; // 30cm - endpoints closer than this to intersections are ignored

      for (const line of allLines) {
        // Check start point
        const startNearIntersection = vertices.some((v) => v.distanceTo(line.start) < endpointThreshold);
        if (!startNearIntersection) {
          vertices.push(line.start.clone());
        }

        // Check end point
        const endNearIntersection = vertices.some((v) => v.distanceTo(line.end) < endpointThreshold);
        if (!endNearIntersection) {
          vertices.push(line.end.clone());
        }
      }
    }

    // Remove duplicates and sort vertices to form a polygon
    const uniqueVertices = this.removeDuplicateVertices(vertices);
    return this.sortVerticesClockwise(uniqueVertices);
  }

  /**
   * Remove duplicate vertices
   */
  private removeDuplicateVertices(vertices: THREE.Vector3[]): THREE.Vector3[] {
    const unique: THREE.Vector3[] = [];
    const threshold = 0.01; // meters

    for (const vertex of vertices) {
      let isDuplicate = false;
      for (const existingVertex of unique) {
        if (vertex.distanceTo(existingVertex) < threshold) {
          isDuplicate = true;
          break;
        }
      }
      if (!isDuplicate) {
        unique.push(vertex);
      }
    }

    return unique;
  }

  /**
   * Sort vertices in clockwise order
   */
  private sortVerticesClockwise(vertices: THREE.Vector3[]): THREE.Vector3[] {
    if (vertices.length < 3) return vertices;

    // Calculate centroid
    const center = this.calculateCenter(vertices);

    // Sort by angle from center
    return vertices.slice().sort((a, b) => {
      const angleA = Math.atan2(a.y - center.y, a.x - center.x);
      const angleB = Math.atan2(b.y - center.y, b.x - center.x);
      return angleA - angleB;
    });
  }

  /**
   * Check if shape is closed (first and last vertices are close)
   */
  private isShapeClosed(vertices: THREE.Vector3[]): boolean {
    if (vertices.length < 3) return false;

    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    return first.distanceTo(last) < 0.5; // 0.5 meters threshold
  }

  /**
   * Calculate center point of vertices
   */
  private calculateCenter(vertices: THREE.Vector3[]): THREE.Vector3 {
    const center = new THREE.Vector3();
    for (const vertex of vertices) {
      center.add(vertex);
    }
    center.divideScalar(vertices.length);
    return center;
  }

  /**
   * Create shape from user-selected lines, following selection order.
   * For each consecutive pair of lines:
   * - If they intersect, use the intersection point
   * - If they don't intersect, use the endpoint closest to the next line's midpoint
   */
  createShapeFromSelectedLines(selectedLines: DetectedLine[]): LineShape | null {
    if (selectedLines.length < 2) return null;

    const vertices: THREE.Vector3[] = [];

    // Process consecutive line pairs in selection order
    for (let i = 0; i < selectedLines.length; i++) {
      const currentLine = selectedLines[i];
      const nextLine = selectedLines[(i + 1) % selectedLines.length];

      // Try to find intersection between current and next line
      const intersection = this.findIntersection(currentLine.start, currentLine.end, nextLine.start, nextLine.end);

      if (intersection) {
        // Lines intersect - use the intersection point
        vertices.push(intersection);
      } else {
        // No intersection - find the endpoint of current line closest to next line's midpoint
        const nextMidpoint = new THREE.Vector3().addVectors(nextLine.start, nextLine.end).multiplyScalar(0.5);

        const distToStart = currentLine.start.distanceTo(nextMidpoint);
        const distToEnd = currentLine.end.distanceTo(nextMidpoint);

        if (distToStart < distToEnd) {
          vertices.push(currentLine.start.clone());
        } else {
          vertices.push(currentLine.end.clone());
        }
      }
    }

    // Remove duplicate consecutive vertices (can happen when lines share endpoints)
    const uniqueVertices = this.removeConsecutiveDuplicates(vertices);

    if (uniqueVertices.length < 3) return null;

    const isClosed = this.isShapeClosed(uniqueVertices);
    const center = this.calculateCenter(uniqueVertices);

    return {
      id: `shape_${this.nextShapeId++}`,
      vertices: uniqueVertices,
      boundaryLines: selectedLines,
      isClosed,
      center,
    };
  }

  /**
   * Remove consecutive duplicate vertices (preserves order, unlike removeDuplicateVertices)
   */
  private removeConsecutiveDuplicates(vertices: THREE.Vector3[]): THREE.Vector3[] {
    if (vertices.length === 0) return [];

    const result: THREE.Vector3[] = [vertices[0]];
    const threshold = 0.01; // meters

    for (let i = 1; i < vertices.length; i++) {
      const prev = result[result.length - 1];
      const current = vertices[i];

      if (current.distanceTo(prev) >= threshold) {
        result.push(current);
      }
    }

    // Also check if first and last are duplicates (for closed shapes)
    if (result.length > 1) {
      const first = result[0];
      const last = result[result.length - 1];
      if (first.distanceTo(last) < threshold) {
        result.pop();
      }
    }

    return result;
  }

  /**
   * Create shape from line prompts using multiple strategies with validation.
   *
   * Tries strategies in order of speed, falling back to more exhaustive methods:
   * 1. Mutual handshake (greedy, works well for convex/simple shapes)
   * 2. Angular ordering by click-point centroid (handles most concave shapes)
   * 3. Angular ordering by intersection-point centroid (handles extreme concavity)
   * 4. Brute force permutation search (N <= 8, guaranteed correct if solution exists)
   *
   * Each result is validated to be a non-self-intersecting polygon before returning.
   */
  createShapeFromPrompts(prompts: LinePrompt[]): LineShape | null {
    return this.withLocalFrame(prompts, () => this.createShapeFromPromptsCore(prompts));
  }

  private createShapeFromPromptsCore(prompts: LinePrompt[]): LineShape | null {
    if (prompts.length === 0) {
      return null;
    }

    if (prompts.length === 1) {
      this.updatePromptConnections(prompts, []);
      return null;
    }

    if (prompts.length === 2) {
      const connections = this.findMutualConnections(prompts);
      this.updatePromptConnections(prompts, connections || []);
      return null;
    }

    // Pre-compute all intersections (shared by all strategies)
    const intersections = this.computeAllIntersections(prompts);

    // Strategy 1: Mutual handshake (existing algorithm)
    const handshakeConnections = this.findMutualConnections(prompts, intersections);
    const handshakeResult = this.tryBuildValidPolygon(handshakeConnections, prompts);

    // After updatePromptConnections, far connections are stripped.
    // If any prompt lost a side, the shape can't be closed — return null.
    const hasDisconnectedSide = (ps: LinePrompt[]): boolean =>
      ps.some((p) => p.positiveLength === undefined || p.negativeLength === undefined);

    if (handshakeResult) {
      this.updatePromptConnections(prompts, handshakeResult.connections);
      if (hasDisconnectedSide(prompts)) return null;
      return {
        id: `shape_${this.nextShapeId++}`,
        vertices: handshakeResult.vertices,
        boundaryLines: [],
        isClosed: true,
        center: handshakeResult.center,
      };
    }

    // Strategy 1.5: Islands merge — start from handshake islands (no crossings),
    // merge them into a single cycle using only crossing-safe swaps.
    if (handshakeConnections && handshakeConnections.length >= prompts.length && !this.checkAllPromptsConnected(prompts.length, handshakeConnections)) {
      const merged = this.mergeCyclesSafe(
        handshakeConnections.map((c) => ({ ...c, point: c.point.clone() })),
        prompts,
        intersections,
      );
      const mergedResult = this.tryBuildValidPolygon(merged, prompts);
      if (mergedResult) {
        this.updatePromptConnections(prompts, mergedResult.connections);
        if (hasDisconnectedSide(prompts)) return null;
        return {
          id: `shape_${this.nextShapeId++}`,
          vertices: mergedResult.vertices,
          boundaryLines: [],
          isClosed: true,
          center: mergedResult.center,
        };
      }
    }

    // Strategy 2: Cascading greedy — connect open sides, steal if stuck
    {
      const cascadingConnections = this.buildConnectionsByCascadingGreedy(prompts, intersections, false);
      const cascadingResult = this.tryBuildValidPolygon(cascadingConnections, prompts);

      if (cascadingResult) {
        this.updatePromptConnections(prompts, cascadingResult.connections);
        if (hasDisconnectedSide(prompts)) return null;
        return {
          id: `shape_${this.nextShapeId++}`,
          vertices: cascadingResult.vertices,
          boundaryLines: [],
          isClosed: true,
          center: cascadingResult.center,
        };
      }
    }

    // No valid polygon found — update connections from best effort
    const bestConnections = handshakeConnections || [];
    this.updatePromptConnections(prompts, bestConnections);
    return null;
  }

  /**
   * Collect unique intersection points from the intersection map.
   */
  private collectUniqueIntersectionPoints(intersections: Map<string, PromptIntersectionData>): THREE.Vector3[] {
    const seen = new Set<string>();
    const points: THREE.Vector3[] = [];

    intersections.forEach((data, key) => {
      const parts = key.split('-');
      const a = parseInt(parts[0], 10);
      const b = parseInt(parts[1], 10);
      const sortedKey = `${Math.min(a, b)}-${Math.max(a, b)}`;
      if (!seen.has(sortedKey)) {
        seen.add(sortedKey);
        points.push(data.point.clone());
      }
    });

    return points;
  }

  /**
   * Check if two line segments intersect (2D intersection test in XY plane).
   * Returns the intersection point if they cross, null otherwise.
   */
  private segmentsIntersect(p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, p4: THREE.Vector3): THREE.Vector3 | null {
    const x1 = p1.x,
      y1 = p1.y;
    const x2 = p2.x,
      y2 = p2.y;
    const x3 = p3.x,
      y3 = p3.y;
    const x4 = p4.x,
      y4 = p4.y;

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

    if (Math.abs(denom) < 0.0001) {
      return null; // Parallel or coincident
    }

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    // Check if intersection is within both line segments
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      const x = x1 + t * (x2 - x1);
      const y = y1 + t * (y2 - y1);
      const z = p1.z + t * (p2.z - p1.z);
      return new THREE.Vector3(x, y, z);
    }

    return null;
  }

  /**
   * Check whether a polygon is simple (non-self-intersecting).
   * Tests all pairs of non-adjacent edges for intersection in the XY plane.
   */
  private isSimplePolygon(vertices: THREE.Vector3[]): boolean {
    const n = vertices.length;
    if (n < 3) return false;

    for (let i = 0; i < n; i++) {
      const p1 = vertices[i];
      const p2 = vertices[(i + 1) % n];

      for (let j = i + 2; j < n; j++) {
        // Skip the pair where the last edge wraps around to share a vertex with the first edge
        if (i === 0 && j === n - 1) continue;

        const p3 = vertices[j];
        const p4 = vertices[(j + 1) % n];

        if (this.segmentsIntersect(p1, p2, p3, p4) !== null) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Attempt to build a valid (simple, non-self-intersecting) polygon from connections.
   * Returns vertices, center, and the (potentially fixed) connections if successful, null otherwise.
   * Does not assign a shape ID — the caller handles that.
   */
  private tryBuildValidPolygon(connections: PromptSideConnection[] | null, prompts: LinePrompt[]): { vertices: THREE.Vector3[]; center: THREE.Vector3; connections: PromptSideConnection[] } | null {
    if (!connections || connections.length < 3) return null;

    if (!this.checkAllPromptsConnected(prompts.length, connections)) return null;

    const path = this.buildPolygonFromConnections(connections, prompts);
    if (!path || path.length < 3) return null;

    const uniqueVertices = this.removeConsecutiveDuplicates(path);
    if (uniqueVertices.length < 3) return null;

    if (this.isSimplePolygon(uniqueVertices)) {
      const center = this.calculateCenter(uniqueVertices);
      return { vertices: uniqueVertices, center, connections };
    }

    // Polygon is self-intersecting — attempt to fix by swapping crossing connections
    const fixed = this.fixPolygonCrossings(connections, prompts);
    if (fixed) {
      const fixedPath = this.buildPolygonFromConnections(fixed, prompts);
      if (fixedPath && fixedPath.length >= 3) {
        const fixedUnique = this.removeConsecutiveDuplicates(fixedPath);
        if (fixedUnique.length >= 3 && this.isSimplePolygon(fixedUnique)) {
          const center = this.calculateCenter(fixedUnique);
          return { vertices: fixedUnique, center, connections: fixed };
        }
      }
    }

    return null;
  }

  /**
   * Fix self-intersecting polygon by detecting crossing polygon edges and
   * swapping the responsible connections.
   *
   * Works by:
   * 1. Building the traversal chain with full side tracking
   * 2. Finding crossing polygon edges
   * 3. For each crossing pair of connections, trying to swap them
   *    by re-pairing the prompt sides to create new non-crossing connections
   */
  private fixPolygonCrossings(connections: PromptSideConnection[], prompts: LinePrompt[]): PromptSideConnection[] | null {
    const fixedConnections = connections.map((c) => ({ ...c, point: c.point.clone() }));
    const maxIterations = 50;
    const fixStartTime = performance.now();
    const fixTimeBudgetMs = 20; // Don't spend more than 20ms trying to fix crossings

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      if (performance.now() - fixStartTime > fixTimeBudgetMs) return null;

      // Build the traversal chain to get polygon edges in order
      const chain = this.buildTraversalChain(fixedConnections, prompts);
      if (!chain || chain.length < 3) return null;

      const n = chain.length;
      const vertices = chain.map((c) => c.point);

      // Find a crossing pair of polygon edges
      let foundI = -1;
      let foundJ = -1;

      for (let i = 0; i < n && foundI === -1; i++) {
        const p1 = vertices[i];
        const p2 = vertices[(i + 1) % n];

        for (let j = i + 2; j < n; j++) {
          if (i === 0 && j === n - 1) continue;

          const p3 = vertices[j];
          const p4 = vertices[(j + 1) % n];

          if (this.segmentsIntersect(p1, p2, p3, p4) !== null) {
            foundI = i;
            foundJ = j;
            break;
          }
        }
      }

      if (foundI === -1) {
        // No crossings — polygon is now simple
        return fixedConnections;
      }

      // Polygon edge foundI goes from chain[foundI].point to chain[(foundI+1)%n].point
      // This edge is produced by the connection at chain[foundI].connectionIndex
      // (connection from chain[foundI].prompt to chain[(foundI+1)%n].prompt)
      //
      // Polygon edge foundJ goes from chain[foundJ].point to chain[(foundJ+1)%n].point
      // This edge is produced by the connection at chain[foundJ].connectionIndex

      const connIdxI = chain[foundI].connectionIndex;
      const connIdxJ = chain[foundJ].connectionIndex;

      const connI = fixedConnections[connIdxI];
      const connJ = fixedConnections[connIdxJ];

      // These two connections cross. Each connection pairs two prompt-sides.
      // connI: (pA, sideA) <-> (pB, sideB)
      // connJ: (pC, sideC) <-> (pD, sideD)
      //
      // We try two swap options:
      // Option 1: (pA, sideA) <-> (pC, sideC) and (pB, sideB) <-> (pD, sideD)
      // Option 2: (pA, sideA) <-> (pD, sideD) and (pB, sideB) <-> (pC, sideC)

      const trySwap = (pa: number, sa: 'pos' | 'neg', pb: number, sb: 'pos' | 'neg', pc: number, sc: 'pos' | 'neg', pd: number, sd: 'pos' | 'neg'): { conn1: PromptSideConnection; conn2: PromptSideConnection } | null => {
        // New connection 1: pa-sa <-> pb-sb
        const pt1 = this.findInfiniteLineIntersection(prompts[pa].worldPoint, prompts[pa].direction, prompts[pb].worldPoint, prompts[pb].direction);
        if (!pt1) return null;

        // Verify sides
        const vecA1 = new THREE.Vector3().subVectors(pt1, prompts[pa].worldPoint);
        const actualSA = vecA1.dot(prompts[pa].direction) >= 0 ? 'pos' : 'neg';
        const vecB1 = new THREE.Vector3().subVectors(pt1, prompts[pb].worldPoint);
        const actualSB = vecB1.dot(prompts[pb].direction) >= 0 ? 'pos' : 'neg';

        if (actualSA !== sa || actualSB !== sb) return null;

        // New connection 2: pc-sc <-> pd-sd
        const pt2 = this.findInfiniteLineIntersection(prompts[pc].worldPoint, prompts[pc].direction, prompts[pd].worldPoint, prompts[pd].direction);
        if (!pt2) return null;

        const vecC2 = new THREE.Vector3().subVectors(pt2, prompts[pc].worldPoint);
        const actualSC = vecC2.dot(prompts[pc].direction) >= 0 ? 'pos' : 'neg';
        const vecD2 = new THREE.Vector3().subVectors(pt2, prompts[pd].worldPoint);
        const actualSD = vecD2.dot(prompts[pd].direction) >= 0 ? 'pos' : 'neg';

        if (actualSC !== sc || actualSD !== sd) return null;

        return {
          conn1: { promptA: pa, promptB: pb, sideA: sa, sideB: sb, point: pt1 },
          conn2: { promptA: pc, promptB: pd, sideA: sc, sideB: sd, point: pt2 },
        };
      };

      // Option 1: swap B endpoints
      const opt1 = trySwap(connI.promptA, connI.sideA, connJ.promptA, connJ.sideA, connI.promptB, connI.sideB, connJ.promptB, connJ.sideB);

      // Option 2: cross-swap
      const opt2 = trySwap(connI.promptA, connI.sideA, connJ.promptB, connJ.sideB, connI.promptB, connI.sideB, connJ.promptA, connJ.sideA);

      // Helper: try applying a swap and verify it preserves the single cycle
      const applySwapIfValid = (swap: { conn1: PromptSideConnection; conn2: PromptSideConnection }, idxI: number, idxJ: number): boolean => {
        const backupI = fixedConnections[idxI];
        const backupJ = fixedConnections[idxJ];
        fixedConnections[idxI] = swap.conn1;
        fixedConnections[idxJ] = swap.conn2;

        // Quick check: does the traversal chain still visit all prompts?
        const testChain = this.buildTraversalChain(fixedConnections, prompts);
        if (testChain && testChain.length === prompts.length) {
          return true;
        }

        // Revert — swap broke the cycle
        fixedConnections[idxI] = backupI;
        fixedConnections[idxJ] = backupJ;
        return false;
      };

      // Try both swap options, preferring the one with shorter edges, but only accept if cycle is preserved
      const candidates = [opt1, opt2].filter(Boolean) as Array<{ conn1: PromptSideConnection; conn2: PromptSideConnection }>;
      // Sort by total edge length (shorter first)
      candidates.sort((a, b) => {
        const lenA = a.conn1.point.distanceTo(a.conn2.point);
        const lenB = b.conn1.point.distanceTo(b.conn2.point);
        return lenA - lenB;
      });

      let swapApplied = false;
      for (const candidate of candidates) {
        if (applySwapIfValid(candidate, connIdxI, connIdxJ)) {
          swapApplied = true;
          break;
        }
      }

      if (!swapApplied) {
        if (performance.now() - fixStartTime > fixTimeBudgetMs) return null;

        // Neither swap for this crossing preserves the cycle — try ALL crossings
        const allCrossings: Array<{ i: number; j: number }> = [];
        for (let ci = 0; ci < n; ci++) {
          const cp1 = vertices[ci];
          const cp2 = vertices[(ci + 1) % n];
          for (let cj = ci + 2; cj < n; cj++) {
            if (ci === 0 && cj === n - 1) continue;
            const cp3 = vertices[cj];
            const cp4 = vertices[(cj + 1) % n];
            if (this.segmentsIntersect(cp1, cp2, cp3, cp4) !== null) {
              allCrossings.push({ i: ci, j: cj });
            }
          }
        }

        let fixedAny = false;
        for (const crossing of allCrossings) {
          if (performance.now() - fixStartTime > fixTimeBudgetMs) break;
          const cIdxI = chain[crossing.i].connectionIndex;
          const cIdxJ = chain[crossing.j].connectionIndex;
          const cI = fixedConnections[cIdxI];
          const cJ = fixedConnections[cIdxJ];

          const alt1 = trySwap(cI.promptA, cI.sideA, cJ.promptA, cJ.sideA, cI.promptB, cI.sideB, cJ.promptB, cJ.sideB);
          const alt2 = trySwap(cI.promptA, cI.sideA, cJ.promptB, cJ.sideB, cI.promptB, cI.sideB, cJ.promptA, cJ.sideA);

          const altCandidates = [alt1, alt2].filter(Boolean) as Array<{ conn1: PromptSideConnection; conn2: PromptSideConnection }>;
          altCandidates.sort((a, b) => {
            const lenA = a.conn1.point.distanceTo(a.conn2.point);
            const lenB = b.conn1.point.distanceTo(b.conn2.point);
            return lenA - lenB;
          });

          for (const altCandidate of altCandidates) {
            if (applySwapIfValid(altCandidate, cIdxI, cIdxJ)) {
              fixedAny = true;
              break;
            }
          }
          if (fixedAny) break;
        }

        if (!fixedAny) {
          if (performance.now() - fixStartTime > fixTimeBudgetMs) return null;

          // No swap preserves the cycle. Try replacing the connection involved in the
          // most crossings with an entirely different connection for the same prompt sides.
          const crossingCount = new Map<number, number>();
          for (const crossing of allCrossings) {
            const ci = chain[crossing.i].connectionIndex;
            const cj = chain[crossing.j].connectionIndex;
            crossingCount.set(ci, (crossingCount.get(ci) || 0) + 1);
            crossingCount.set(cj, (crossingCount.get(cj) || 0) + 1);
          }

          // Sort by crossing count descending — try replacing the worst offender first
          const sorted = [...crossingCount.entries()].sort((a, b) => b[1] - a[1]);

          let replaced = false;
          for (const [worstIdx] of sorted) {
            if (performance.now() - fixStartTime > fixTimeBudgetMs) break;
            const worstConn = fixedConnections[worstIdx];

            // Try to find an alternative connection for each of the two prompt-sides
            // that this connection serves, pairing them with different partners
            const sidesA = { prompt: worstConn.promptA, side: worstConn.sideA };
            const sidesB = { prompt: worstConn.promptB, side: worstConn.sideB };

            // For each side, find all possible partners from the intersections map
            for (const [primarySide, otherSide] of [
              [sidesA, sidesB],
              [sidesB, sidesA],
            ] as const) {
              // Find alternative connections for primarySide
              for (let j = 0; j < prompts.length; j++) {
                if (j === primarySide.prompt || j === otherSide.prompt) continue;

                // Check if prompt j has a connection that could be swapped
                const jConnIdx = fixedConnections.findIndex((c) => c && (c.promptA === j || c.promptB === j) && c !== fixedConnections[worstIdx]);
                if (jConnIdx === -1) continue;

                // Try: replace worstConn with (primarySide ↔ some side of j),
                // and replace j's connection with (otherSide ↔ j's displaced partner)
                const jConn = fixedConnections[jConnIdx];
                const jSideInConn = jConn.promptA === j ? jConn.sideA : jConn.sideB;
                const jPartner = jConn.promptA === j ? { prompt: jConn.promptB, side: jConn.sideB } : { prompt: jConn.promptA, side: jConn.sideA };

                // New connection 1: primarySide ↔ j (same side j uses in its current connection)
                const pt1 = this.findInfiniteLineIntersection(prompts[primarySide.prompt].worldPoint, prompts[primarySide.prompt].direction, prompts[j].worldPoint, prompts[j].direction);
                if (!pt1) continue;

                const vec1p = new THREE.Vector3().subVectors(pt1, prompts[primarySide.prompt].worldPoint);
                const actual1p = vec1p.dot(prompts[primarySide.prompt].direction) >= 0 ? 'pos' : 'neg';
                const vec1j = new THREE.Vector3().subVectors(pt1, prompts[j].worldPoint);
                const actual1j = vec1j.dot(prompts[j].direction) >= 0 ? 'pos' : 'neg';

                if (actual1p !== primarySide.side || actual1j !== jSideInConn) continue;

                // New connection 2: otherSide ↔ jPartner
                const pt2 = this.findInfiniteLineIntersection(prompts[otherSide.prompt].worldPoint, prompts[otherSide.prompt].direction, prompts[jPartner.prompt].worldPoint, prompts[jPartner.prompt].direction);
                if (!pt2) continue;

                const vec2o = new THREE.Vector3().subVectors(pt2, prompts[otherSide.prompt].worldPoint);
                const actual2o = vec2o.dot(prompts[otherSide.prompt].direction) >= 0 ? 'pos' : 'neg';
                const vec2jp = new THREE.Vector3().subVectors(pt2, prompts[jPartner.prompt].worldPoint);
                const actual2jp = vec2jp.dot(prompts[jPartner.prompt].direction) >= 0 ? 'pos' : 'neg';

                if (actual2o !== otherSide.side || actual2jp !== jPartner.side) continue;

                // Apply tentatively and check cycle + crossing improvement
                const backupWorst = fixedConnections[worstIdx];
                const backupJ = fixedConnections[jConnIdx];

                fixedConnections[worstIdx] = {
                  promptA: primarySide.prompt,
                  promptB: j,
                  sideA: primarySide.side,
                  sideB: jSideInConn,
                  point: pt1,
                };
                fixedConnections[jConnIdx] = {
                  promptA: otherSide.prompt,
                  promptB: jPartner.prompt,
                  sideA: otherSide.side,
                  sideB: jPartner.side,
                  point: pt2,
                };

                if (this.checkAllPromptsConnected(prompts.length, fixedConnections)) {
                  // Check if this actually reduces crossings
                  const newChain = this.buildTraversalChain(fixedConnections, prompts);
                  if (newChain) {
                    let newCrossings = 0;
                    const nv = newChain.length;
                    const newVerts = newChain.map((c) => c.point);
                    for (let ci = 0; ci < nv; ci++) {
                      for (let cj = ci + 2; cj < nv; cj++) {
                        if (ci === 0 && cj === nv - 1) continue;
                        if (this.segmentsIntersect(newVerts[ci], newVerts[(ci + 1) % nv], newVerts[cj], newVerts[(cj + 1) % nv]) !== null) {
                          newCrossings++;
                        }
                      }
                    }
                    if (newCrossings < allCrossings.length) {
                      replaced = true;
                      break;
                    }
                  }
                }

                // Revert
                fixedConnections[worstIdx] = backupWorst;
                fixedConnections[jConnIdx] = backupJ;
              }
              if (replaced) break;
            }
            if (replaced) break;
          }

          if (!replaced) {
            return null;
          }
          continue;
        }
        continue;
      }
    }

    return null; // Too many iterations
  }

  /**
   * Build the traversal chain with full info: for each step, track the prompt index,
   * the exit side, the connection point (polygon vertex), and which connection index
   * produced this edge.
   */
  private buildTraversalChain(connections: PromptSideConnection[], prompts: LinePrompt[]): Array<{ prompt: number; exitSide: 'pos' | 'neg'; point: THREE.Vector3; connectionIndex: number }> | null {
    if (connections.length < 3) return null;

    // Build adjacency: for each prompt+side, which connection leads where?
    const adjacency = new Map<
      string,
      {
        nextPrompt: number;
        nextSide: 'pos' | 'neg';
        point: THREE.Vector3;
        connectionIndex: number;
      }
    >();

    for (let idx = 0; idx < connections.length; idx++) {
      const conn = connections[idx];

      const exitSideB: 'pos' | 'neg' = conn.sideB === 'pos' ? 'neg' : 'pos';
      adjacency.set(`${conn.promptA}-${conn.sideA}`, {
        nextPrompt: conn.promptB,
        nextSide: exitSideB,
        point: conn.point,
        connectionIndex: idx,
      });

      const exitSideA: 'pos' | 'neg' = conn.sideA === 'pos' ? 'neg' : 'pos';
      adjacency.set(`${conn.promptB}-${conn.sideB}`, {
        nextPrompt: conn.promptA,
        nextSide: exitSideA,
        point: conn.point,
        connectionIndex: idx,
      });
    }

    const startPrompt = connections[0].promptA;
    const startSide = connections[0].sideA;

    const chain: Array<{ prompt: number; exitSide: 'pos' | 'neg'; point: THREE.Vector3; connectionIndex: number }> = [];
    const visited = new Set<string>();

    let currentPrompt = startPrompt;
    let currentSide = startSide;

    for (let step = 0; step < prompts.length * 2; step++) {
      const key = `${currentPrompt}-${currentSide}`;
      if (visited.has(key)) break;
      visited.add(key);

      const next = adjacency.get(key);
      if (!next) break;

      chain.push({
        prompt: currentPrompt,
        exitSide: currentSide,
        point: next.point.clone(),
        connectionIndex: next.connectionIndex,
      });

      currentPrompt = next.nextPrompt;
      currentSide = next.nextSide;
    }

    return chain.length >= 3 ? chain : null;
  }

  /**
   * Build connections from a cyclic prompt ordering and pre-computed intersections.
   * The sides are determined by the intersection geometry — each consecutive pair
   * of prompts in the ordering uses their mutual intersection point, and the sides
   * are taken directly from the intersection data.
   *
   * For each prompt, it must use one side for the connection to its predecessor
   * and the opposite side for the connection to its successor. If the intersection
   * data doesn't allow this, the ordering is invalid.
   */
  private buildConnectionsFromOrdering(ordering: number[], intersections: Map<string, PromptIntersectionData>): PromptSideConnection[] | null {
    const n = ordering.length;
    const connections: PromptSideConnection[] = [];

    // For each consecutive pair, look up their intersection
    const edgeSides: Array<{ sideFrom: 'pos' | 'neg'; sideTo: 'pos' | 'neg'; point: THREE.Vector3 }> = [];

    for (let k = 0; k < n; k++) {
      const i = ordering[k];
      const j = ordering[(k + 1) % n];
      const key = `${i}-${j}`;
      const data = intersections.get(key);
      if (!data) return null;
      edgeSides.push({ sideFrom: data.sideI, sideTo: data.sideJ, point: data.point.clone() });
    }

    // Verify consistency: for each prompt, the side used by the incoming edge
    // must be the opposite of the side used by the outgoing edge
    for (let k = 0; k < n; k++) {
      const prevEdge = edgeSides[(k - 1 + n) % n]; // edge from prev -> this prompt
      const nextEdge = edgeSides[k]; // edge from this prompt -> next

      // prevEdge.sideTo is the side this prompt uses for the incoming connection
      // nextEdge.sideFrom is the side this prompt uses for the outgoing connection
      // They must be opposite (enter one side, exit the other)
      if (prevEdge.sideTo === nextEdge.sideFrom) {
        return null; // Same side used for both — invalid ordering
      }
    }

    // Build connections
    for (let k = 0; k < n; k++) {
      const i = ordering[k];
      const j = ordering[(k + 1) % n];
      const edge = edgeSides[k];

      connections.push({
        promptA: i,
        promptB: j,
        sideA: edge.sideFrom,
        sideB: edge.sideTo,
        point: edge.point,
      });
    }

    return connections;
  }

  /**
   * Build connections by cascading greedy matching with connection stealing.
   *
   * Algorithm:
   * 1. All prompt sides (pos/neg) start as "open" — 2N open sides total (must be even).
   * 2. Pick the open side with the nearest open partner (by intersection distance).
   *    Accept the connection only if it doesn't cross any already-established connection.
   * 3. If no valid open-open match exists, "steal" from the nearest already-connected
   *    prompt side: take over that connection, and the displaced side becomes the new
   *    open side (cascading).
   * 4. Stolen connections are locked so the same cascade can't steal them back.
   *    When we move on to the next top-level open side, locks are lifted.
   * 5. If open side count is odd, the shape can't close — bail out.
   */
  private buildConnectionsByCascadingGreedy(prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>, useConstraintPropagation = false): PromptSideConnection[] | null {
    const n = prompts.length;
    if (n < 3) return null;

    // Each prompt has two sides. Track which are open vs connected.
    // A "side key" is `${promptIndex}-${side}` e.g. "0-pos", "3-neg"
    type SideKey = string;
    const sideKey = (prompt: number, side: 'pos' | 'neg'): SideKey => `${prompt}-${side}`;
    const parseSideKey = (key: SideKey): { prompt: number; side: 'pos' | 'neg' } => {
      const dash = key.indexOf('-');
      return { prompt: parseInt(key.substring(0, dash), 10), side: key.substring(dash + 1) as 'pos' | 'neg' };
    };

    // Connection map: sideKey -> the connection it participates in (index into connections array)
    const sideToConn = new Map<SideKey, number>();
    const connections: (PromptSideConnection | null)[] = [];
    const openSides = new Set<SideKey>();

    // Initialize: all sides are open
    for (let i = 0; i < n; i++) {
      openSides.add(sideKey(i, 'pos'));
      openSides.add(sideKey(i, 'neg'));
    }

    // 2N sides must be even (always true), and we need N connections
    if (openSides.size % 2 !== 0) return null;

    // Pre-build sorted candidate lists for each side:
    // For side (i, sideI), find all sides (j, sideJ) it can connect to, sorted by distance.
    const candidates = new Map<SideKey, Array<{ target: SideKey; dist: number; point: THREE.Vector3 }>>();

    for (let i = 0; i < n; i++) {
      for (const si of ['pos', 'neg'] as const) {
        const key = sideKey(i, si);
        const cands: Array<{ target: SideKey; dist: number; point: THREE.Vector3 }> = [];

        for (let j = 0; j < n; j++) {
          if (j === i) continue;
          const intKey = `${i}-${j}`;
          const data = intersections.get(intKey);
          if (!data) continue;
          if (data.sideI !== si) continue;

          const dist = data.distFromI + data.distFromJ;

          cands.push({
            target: sideKey(j, data.sideJ),
            dist,
            point: data.point,
          });
        }

        cands.sort((a, b) => a.dist - b.dist);
        candidates.set(key, cands);
      }
    }

    // Constraint propagation: lock forced connections before greedy.
    // If a side has only one valid candidate, that connection is forced.
    if (useConstraintPropagation) {
      let changed = true;
      while (changed) {
        changed = false;
        for (const key of [...openSides]) {
          const cands = candidates.get(key);
          if (!cands) continue;

          const available = cands.filter((c) => openSides.has(c.target));
          if (available.length === 0) continue;

          if (available.length === 1) {
            const cand = available[0];
            const src = parseSideKey(key);
            const tgt = parseSideKey(cand.target);

            const connIdx = connections.length;
            connections.push({
              promptA: src.prompt,
              promptB: tgt.prompt,
              sideA: src.side,
              sideB: tgt.side,
              point: cand.point.clone(),
            });

            openSides.delete(key);
            openSides.delete(cand.target);
            sideToConn.set(key, connIdx);
            sideToConn.set(cand.target, connIdx);
            changed = true;
          }
        }
      }
    }

    // Helper: does segment (a1->a2) cross segment (b1->b2)?
    const segCross = (a1: THREE.Vector3, a2: THREE.Vector3, b1: THREE.Vector3, b2: THREE.Vector3): boolean => {
      const x1 = a1.x,
        y1 = a1.y,
        x2 = a2.x,
        y2 = a2.y;
      const x3 = b1.x,
        y3 = b1.y,
        x4 = b2.x,
        y4 = b2.y;
      const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
      if (Math.abs(denom) < 0.0001) return false;
      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
      const eps = 0.001;
      return t > eps && t < 1 - eps && u > eps && u < 1 - eps;
    };

    // Check if a new connection between promptI and promptJ would cross existing ones.
    // Uses both worldPoint lines and intersection point proximity to detect crossings.
    const wouldCross = (promptI: number, promptJ: number, newPt: THREE.Vector3): boolean => {
      const pI = prompts[promptI].worldPoint;
      const pJ = prompts[promptJ].worldPoint;

      for (const conn of connections) {
        if (!conn) continue; // nulled-out stolen connections
        if (conn.promptA === promptI || conn.promptA === promptJ || conn.promptB === promptI || conn.promptB === promptJ) continue;

        const pA = prompts[conn.promptA].worldPoint;
        const pB = prompts[conn.promptB].worldPoint;

        // Check 1: worldPoint lines cross (cheap proxy)
        if (segCross(pI, pJ, pA, pB)) return true;

        // Check 2: intersection points cross
        // The actual polygon vertices are the intersection points, so also check
        // if the new intersection point's "reach" segments cross existing ones.
        if (segCross(pI, newPt, pA, conn.point)) return true;
        if (segCross(newPt, pJ, conn.point, pB)) return true;
      }
      return false;
    };

    // Main loop: keep connecting open sides until none remain
    const maxIterations = n * n * 4; // Safety limit
    const startTime = performance.now();
    const maxTimeMs = 100; // Hard time budget — abort if no solution found within 100ms
    let iteration = 0;

    while (openSides.size > 0 && iteration < maxIterations && performance.now() - startTime < maxTimeMs) {
      iteration++;

      // Find the best open-open pair: the open side with the nearest open partner
      let bestSrc: SideKey | null = null;
      let bestTgt: SideKey | null = null;
      let bestDist = Infinity;
      let bestPoint: THREE.Vector3 | null = null;

      for (const srcKey of openSides) {
        const { prompt: srcPrompt } = parseSideKey(srcKey);
        const cands = candidates.get(srcKey);
        if (!cands) continue;

        for (const cand of cands) {
          if (cand.dist >= bestDist) break; // sorted, no point continuing
          if (!openSides.has(cand.target)) continue;

          const { prompt: tgtPrompt } = parseSideKey(cand.target);

          // The other side of the target prompt must also be open or connectable
          // (we need both sides of each prompt to eventually be connected)
          if (!wouldCross(srcPrompt, tgtPrompt, cand.point)) {
            bestSrc = srcKey;
            bestTgt = cand.target;
            bestDist = cand.dist;
            bestPoint = cand.point;
            break; // this is the nearest valid for this src
          }
        }
      }

      if (bestSrc && bestTgt && bestPoint) {
        // Connect these two open sides
        const src = parseSideKey(bestSrc);
        const tgt = parseSideKey(bestTgt);

        const connIdx = connections.length;
        connections.push({
          promptA: src.prompt,
          promptB: tgt.prompt,
          sideA: src.side,
          sideB: tgt.side,
          point: bestPoint.clone(),
        });

        openSides.delete(bestSrc);
        openSides.delete(bestTgt);
        sideToConn.set(bestSrc, connIdx);
        sideToConn.set(bestTgt, connIdx);
        continue;
      }

      // No open-open pair found without crossing — try stealing
      // Pick the first open side and try to steal from the nearest connected side
      const lockedSides = new Set<SideKey>();
      let stealSuccess = false;

      // Get the open sides sorted by their nearest candidate distance (most constrained first)
      const sortedOpen = [...openSides].sort((a, b) => {
        const candsA = candidates.get(a);
        const candsB = candidates.get(b);
        const distA = candsA?.[0]?.dist ?? Infinity;
        const distB = candsB?.[0]?.dist ?? Infinity;
        return distA - distB;
      });

      for (const openKey of sortedOpen) {
        if (!openSides.has(openKey)) continue; // may have been resolved by cascading
        const cands = candidates.get(openKey);
        if (!cands) continue;

        // Cascading steal loop
        let currentOpen = openKey;
        let cascadeDepth = 0;
        const maxCascade = n * 2;

        while (cascadeDepth < maxCascade) {
          cascadeDepth++;
          const { prompt: curPrompt } = parseSideKey(currentOpen);
          const curCands = candidates.get(currentOpen);
          if (!curCands) break;

          let stolen = false;

          for (const cand of curCands) {
            if (openSides.has(cand.target)) {
              // Found an open target! Connect directly
              const src = parseSideKey(currentOpen);
              const tgt = parseSideKey(cand.target);

              if (!wouldCross(src.prompt, tgt.prompt, cand.point)) {
                const connIdx = connections.length;
                connections.push({
                  promptA: src.prompt,
                  promptB: tgt.prompt,
                  sideA: src.side,
                  sideB: tgt.side,
                  point: cand.point.clone(),
                });

                openSides.delete(currentOpen);
                openSides.delete(cand.target);
                sideToConn.set(currentOpen, connIdx);
                sideToConn.set(cand.target, connIdx);
                stealSuccess = true;
                stolen = true;
                break;
              }
            }

            // Try stealing from a connected side
            if (lockedSides.has(cand.target)) continue;
            if (!sideToConn.has(cand.target)) continue;

            const { prompt: tgtPrompt } = parseSideKey(cand.target);
            if (tgtPrompt === curPrompt) continue;

            if (wouldCross(curPrompt, tgtPrompt, cand.point)) continue;

            // Steal: disconnect the target side from its current connection
            const oldConnIdx = sideToConn.get(cand.target);
            if (oldConnIdx === undefined) continue;
            const oldConn = connections[oldConnIdx];
            if (!oldConn) continue;

            // Find which side of the old connection was the target
            const tgtParsed = parseSideKey(cand.target);
            let displacedKey: SideKey;
            if (oldConn.promptA === tgtParsed.prompt && oldConn.sideA === tgtParsed.side) {
              displacedKey = sideKey(oldConn.promptB, oldConn.sideB);
            } else {
              displacedKey = sideKey(oldConn.promptA, oldConn.sideA);
            }

            // Remove old connection
            sideToConn.delete(cand.target);
            sideToConn.delete(displacedKey);

            // Null out old connection (mark as removed)
            connections[oldConnIdx] = null;

            // Create new connection: currentOpen <-> cand.target
            const src = parseSideKey(currentOpen);
            const connIdx = connections.length;
            connections.push({
              promptA: src.prompt,
              promptB: tgtParsed.prompt,
              sideA: src.side,
              sideB: tgtParsed.side,
              point: cand.point.clone(),
            });

            openSides.delete(currentOpen);
            sideToConn.set(currentOpen, connIdx);
            sideToConn.set(cand.target, connIdx);

            // Lock the stolen connection so cascade can't steal it back
            lockedSides.add(currentOpen);
            lockedSides.add(cand.target);

            // The displaced side is now open — cascade
            openSides.add(displacedKey);
            currentOpen = displacedKey;
            stolen = true;
            stealSuccess = true;
            break;
          }

          if (!stolen || openSides.size === 0) break;
          if (!openSides.has(currentOpen)) break; // was resolved
        }

        // Lift locks after processing this open side's cascade
        lockedSides.clear();

        if (openSides.size === 0) break;
      }

      if (!stealSuccess) {
        // Completely stuck — bail
        break;
      }
    }

    // Filter out null entries (removed connections) and check we have enough
    let finalConnections = connections.filter((c) => c !== null);

    if (finalConnections.length < n) return null;

    // Merge disjoint cycles: the greedy may produce multiple loops instead of one.
    // Fix by swapping connections between cycles to bridge them.
    finalConnections = this.mergeCycles(finalConnections, prompts, intersections);

    return finalConnections;
  }

  /**
   * Detect disjoint cycles in a set of connections and merge them into a single cycle.
   *
   * When two cycles exist, pick one connection from each cycle and swap their endpoints
   * to bridge the two cycles into one. Repeat until only one cycle remains.
   */
  private mergeCycles(connections: PromptSideConnection[], prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>): PromptSideConnection[] {
    const n = prompts.length;
    const maxAttempts = n;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Build adjacency and find cycles
      const adj = new Map<string, { nextPrompt: number; nextSide: 'pos' | 'neg'; connIdx: number }>();

      for (let ci = 0; ci < connections.length; ci++) {
        const conn = connections[ci];
        const exitB: 'pos' | 'neg' = conn.sideB === 'pos' ? 'neg' : 'pos';
        adj.set(`${conn.promptA}-${conn.sideA}`, { nextPrompt: conn.promptB, nextSide: exitB, connIdx: ci });
        const exitA: 'pos' | 'neg' = conn.sideA === 'pos' ? 'neg' : 'pos';
        adj.set(`${conn.promptB}-${conn.sideB}`, { nextPrompt: conn.promptA, nextSide: exitA, connIdx: ci });
      }

      // Trace all cycles
      const promptToCycle = new Map<number, number>();
      const cycles: Array<Array<{ prompt: number; side: 'pos' | 'neg'; connIdx: number }>> = [];
      const visitedKeys = new Set<string>();

      for (let startP = 0; startP < n; startP++) {
        if (promptToCycle.has(startP)) continue;

        for (const startSide of ['pos', 'neg'] as const) {
          const startKey = `${startP}-${startSide}`;
          if (!adj.has(startKey) || visitedKeys.has(startKey)) continue;

          const cycle: Array<{ prompt: number; side: 'pos' | 'neg'; connIdx: number }> = [];
          let cur = startP;
          let side = startSide;

          while (true) {
            const k = `${cur}-${side}`;
            if (visitedKeys.has(k)) break;
            visitedKeys.add(k);

            const next = adj.get(k);
            if (!next) break;

            cycle.push({ prompt: cur, side, connIdx: next.connIdx });
            promptToCycle.set(cur, cycles.length);
            cur = next.nextPrompt;
            side = next.nextSide;
          }

          if (cycle.length > 0) {
            cycles.push(cycle);
            break;
          }
        }
      }

      if (cycles.length <= 1) return connections; // Single cycle — done!

      // Merge first two cycles by swapping connections
      const cycleA = cycles[0];
      const cycleB = cycles[1];

      // Try all pairs of connections (one from each cycle) and find a swap
      // that bridges them. A swap replaces connI: (pA,sA)-(pB,sB) and
      // connJ: (pC,sC)-(pD,sD) with (pA,sA)-(pC,sC) and (pB,sB)-(pD,sD)
      // (or the other cross-pairing).
      let merged = false;

      for (const stepA of cycleA) {
        if (merged) break;
        const connI = connections[stepA.connIdx];

        for (const stepB of cycleB) {
          if (merged) break;
          const connJ = connections[stepB.connIdx];

          // Try two swap options
          const swaps: Array<[number, 'pos' | 'neg', number, 'pos' | 'neg', number, 'pos' | 'neg', number, 'pos' | 'neg']> = [
            // Option 1: (A.promptA, A.sideA) with (B.promptA, B.sideA),
            //           (A.promptB, A.sideB) with (B.promptB, B.sideB)
            [connI.promptA, connI.sideA, connJ.promptA, connJ.sideA, connI.promptB, connI.sideB, connJ.promptB, connJ.sideB],
            // Option 2: (A.promptA, A.sideA) with (B.promptB, B.sideB),
            //           (A.promptB, A.sideB) with (B.promptA, B.sideA)
            [connI.promptA, connI.sideA, connJ.promptB, connJ.sideB, connI.promptB, connI.sideB, connJ.promptA, connJ.sideA],
          ];

          for (const [pA, sA, pB, sB, pC, sC, pD, sD] of swaps) {
            // Check intersection exists for new pair 1: pA-sA <-> pB-sB
            const key1 = `${pA}-${pB}`;
            const data1 = intersections.get(key1);
            if (!data1) continue;
            if (data1.sideI !== sA || data1.sideJ !== sB) continue;

            // Check intersection exists for new pair 2: pC-sC <-> pD-sD
            const key2 = `${pC}-${pD}`;
            const data2 = intersections.get(key2);
            if (!data2) continue;
            if (data2.sideI !== sC || data2.sideJ !== sD) continue;

            // Replace the two old connections with two new ones
            connections[stepA.connIdx] = {
              promptA: pA,
              promptB: pB,
              sideA: sA,
              sideB: sB,
              point: data1.point.clone(),
            };
            connections[stepB.connIdx] = {
              promptA: pC,
              promptB: pD,
              sideA: sC,
              sideB: sD,
              point: data2.point.clone(),
            };
            merged = true;
            break;
          }
        }
      }

      if (!merged) return connections; // Can't merge — return as-is
    }

    return connections;
  }

  /**
   * Crossing-aware cycle merger. Starts from multiple clean islands (no crossings)
   * and merges them into a single cycle by swapping connection pairs between cycles.
   * Only accepts swaps that don't introduce polygon self-intersections.
   * Prefers shorter bridge connections.
   */
  private mergeCyclesSafe(connections: PromptSideConnection[], prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>): PromptSideConnection[] {
    const n = prompts.length;
    const mergeStartTime = performance.now();
    const mergeTimeBudgetMs = 30;

    for (let attempt = 0; attempt < n; attempt++) {
      if (performance.now() - mergeStartTime > mergeTimeBudgetMs) return connections;
      // Build adjacency and find cycles
      const adj = new Map<string, { nextPrompt: number; nextSide: 'pos' | 'neg'; connIdx: number }>();

      for (let ci = 0; ci < connections.length; ci++) {
        const conn = connections[ci];
        const exitB: 'pos' | 'neg' = conn.sideB === 'pos' ? 'neg' : 'pos';
        adj.set(`${conn.promptA}-${conn.sideA}`, { nextPrompt: conn.promptB, nextSide: exitB, connIdx: ci });
        const exitA: 'pos' | 'neg' = conn.sideA === 'pos' ? 'neg' : 'pos';
        adj.set(`${conn.promptB}-${conn.sideB}`, { nextPrompt: conn.promptA, nextSide: exitA, connIdx: ci });
      }

      // Trace all cycles
      const promptToCycle = new Map<number, number>();
      const cycles: Array<Array<{ prompt: number; side: 'pos' | 'neg'; connIdx: number }>> = [];
      const visitedKeys = new Set<string>();

      for (let startP = 0; startP < n; startP++) {
        if (promptToCycle.has(startP)) continue;

        for (const startSide of ['pos', 'neg'] as const) {
          const startKey = `${startP}-${startSide}`;
          if (!adj.has(startKey) || visitedKeys.has(startKey)) continue;

          const cycle: Array<{ prompt: number; side: 'pos' | 'neg'; connIdx: number }> = [];
          let cur = startP;
          let side = startSide;

          while (true) {
            const k = `${cur}-${side}`;
            if (visitedKeys.has(k)) break;
            visitedKeys.add(k);

            const next = adj.get(k);
            if (!next) break;

            cycle.push({ prompt: cur, side, connIdx: next.connIdx });
            promptToCycle.set(cur, cycles.length);
            cur = next.nextPrompt;
            side = next.nextSide;
          }

          if (cycle.length > 0) {
            cycles.push(cycle);
            break;
          }
        }
      }

      if (cycles.length <= 1) return connections; // Single cycle — done!

      // Merge first two cycles by swapping connections, preferring shortest bridge
      const cycleA = cycles[0];
      const cycleB = cycles[1];

      // Collect all valid bridge swaps, sorted by total distance
      const bridgeCandidates: Array<{
        idxA: number;
        idxB: number;
        conn1: PromptSideConnection;
        conn2: PromptSideConnection;
        totalDist: number;
      }> = [];

      for (const stepA of cycleA) {
        const connI = connections[stepA.connIdx];

        for (const stepB of cycleB) {
          const connJ = connections[stepB.connIdx];

          const swaps: Array<[number, 'pos' | 'neg', number, 'pos' | 'neg', number, 'pos' | 'neg', number, 'pos' | 'neg']> = [
            [connI.promptA, connI.sideA, connJ.promptA, connJ.sideA, connI.promptB, connI.sideB, connJ.promptB, connJ.sideB],
            [connI.promptA, connI.sideA, connJ.promptB, connJ.sideB, connI.promptB, connI.sideB, connJ.promptA, connJ.sideA],
          ];

          for (const [pA, sA, pB, sB, pC, sC, pD, sD] of swaps) {
            const key1 = `${pA}-${pB}`;
            const data1 = intersections.get(key1);
            if (!data1 || data1.sideI !== sA || data1.sideJ !== sB) continue;

            const key2 = `${pC}-${pD}`;
            const data2 = intersections.get(key2);
            if (!data2 || data2.sideI !== sC || data2.sideJ !== sD) continue;

            const totalDist = data1.distFromI + data1.distFromJ + data2.distFromI + data2.distFromJ;

            bridgeCandidates.push({
              idxA: stepA.connIdx,
              idxB: stepB.connIdx,
              conn1: { promptA: pA, promptB: pB, sideA: sA, sideB: sB, point: data1.point.clone() },
              conn2: { promptA: pC, promptB: pD, sideA: sC, sideB: sD, point: data2.point.clone() },
              totalDist,
            });
          }
        }
      }

      // Sort by total distance — prefer short bridges
      bridgeCandidates.sort((a, b) => a.totalDist - b.totalDist);

      let merged = false;
      for (const candidate of bridgeCandidates) {
        // Tentatively apply
        const backupA = connections[candidate.idxA];
        const backupB = connections[candidate.idxB];
        connections[candidate.idxA] = candidate.conn1;
        connections[candidate.idxB] = candidate.conn2;

        // Check: does this produce a simple (non-crossing) polygon if we're down to 1 cycle?
        // At minimum, verify the merged result doesn't create crossings
        const chain = this.buildTraversalChain(connections, prompts);
        if (chain && chain.length >= 3) {
          const vertices = chain.map((c) => c.point);
          const nv = vertices.length;
          let hasCrossing = false;

          // Only check if the NEW edges (involving the two new connections) cross existing edges
          // This is O(n) instead of O(n²)
          const newConnIdxSet = new Set([candidate.idxA, candidate.idxB]);
          for (let i = 0; i < nv && !hasCrossing; i++) {
            const ci = chain[i].connectionIndex;
            if (!newConnIdxSet.has(ci)) continue;

            const p1 = vertices[i];
            const p2 = vertices[(i + 1) % nv];

            for (let j = 0; j < nv; j++) {
              if (j === i || j === (i + nv - 1) % nv || (i + 1) % nv === j) continue;
              const p3 = vertices[j];
              const p4 = vertices[(j + 1) % nv];
              if (this.segmentsIntersect(p1, p2, p3, p4) !== null) {
                hasCrossing = true;
                break;
              }
            }
          }

          if (!hasCrossing) {
            merged = true;
            break;
          }
        }

        // Revert
        connections[candidate.idxA] = backupA;
        connections[candidate.idxB] = backupB;
      }

      if (!merged) return connections; // Can't merge without crossings — return as-is
    }

    return connections;
  }

  /**
   * Try a specific cyclic ordering of prompt indices and return connections + perimeter
   * if the resulting polygon is valid (non-self-intersecting with all sides connected).
   */
  private tryOrdering(ordering: number[], prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>): { connections: PromptSideConnection[]; perimeter: number } | null {
    const n = ordering.length;
    const connections: PromptSideConnection[] = [];
    const vertices: THREE.Vector3[] = [];

    for (let k = 0; k < n; k++) {
      const i = ordering[k];
      const j = ordering[(k + 1) % n];

      const key = `${i}-${j}`;
      const data = intersections.get(key);
      if (!data) return null;

      connections.push({
        promptA: i,
        promptB: j,
        sideA: data.sideI,
        sideB: data.sideJ,
        point: data.point.clone(),
      });

      vertices.push(data.point.clone());
    }

    if (!this.checkAllPromptsConnected(n, connections)) return null;
    if (!this.isSimplePolygon(vertices)) return null;

    let perimeter = 0;
    for (let k = 0; k < vertices.length; k++) {
      perimeter += vertices[k].distanceTo(vertices[(k + 1) % vertices.length]);
    }

    return { connections, perimeter };
  }

  /**
   * For small prompt counts (N <= 8), try all cyclic permutations of prompt ordering
   * and select the one that produces a valid non-self-intersecting polygon with
   * minimum perimeter. Returns null if no valid permutation is found.
   */
  private buildConnectionsByBruteForce(prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>): PromptSideConnection[] | null {
    const n = prompts.length;
    if (n < 3 || n > 8) return null;

    // Fix index 0 to break cyclic symmetry, permute the rest
    const indices = Array.from({ length: n - 1 }, (_, i) => i + 1);

    let bestConnections: PromptSideConnection[] | null = null;
    let bestPerimeter = Infinity;

    const permute = (arr: number[], start: number): void => {
      if (start === arr.length) {
        const ordering = [0, ...arr];
        const result = this.tryOrdering(ordering, prompts, intersections);
        if (result && result.perimeter < bestPerimeter) {
          bestPerimeter = result.perimeter;
          bestConnections = result.connections;
        }
        return;
      }

      for (let i = start; i < arr.length; i++) {
        [arr[start], arr[i]] = [arr[i], arr[start]];
        permute(arr, start + 1);
        [arr[start], arr[i]] = [arr[i], arr[start]];
      }
    };

    permute(indices, 0);

    return bestConnections;
  }

  /**
   * Build connections by nearest-neighbor chain traversal — order-independent.
   *
   * For each starting prompt, greedily builds a cyclic ordering by always
   * jumping to the nearest unvisited prompt (by intersection distance).
   * The "nearest" is measured by the combined distance (distFromI + distFromJ)
   * of the intersection point.
   *
   * Tries every prompt as a starting point and picks the best valid polygon
   * (shortest perimeter, non-self-intersecting).
   */
  private buildConnectionsByNearestNeighbor(prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>): PromptSideConnection[] | null {
    const n = prompts.length;
    if (n < 3) return null;

    let bestConnections: PromptSideConnection[] | null = null;
    let bestPerimeter = Infinity;

    for (let startIdx = 0; startIdx < n; startIdx++) {
      const ordering = this.buildNearestNeighborOrdering(startIdx, n, intersections);
      if (!ordering || ordering.length !== n) continue;

      const result = this.tryOrdering(ordering, prompts, intersections);
      if (result && result.perimeter < bestPerimeter) {
        bestPerimeter = result.perimeter;
        bestConnections = result.connections;
      }
    }

    return bestConnections;
  }

  /**
   * Fix NN orderings by iteratively relocating prompts involved in crossings.
   *
   * For each NN ordering that has crossings:
   * 1. Find the prompt involved in the most crossing edges
   * 2. Among ties, pick the one with more total intersections (least constrained = most likely wrong)
   * 3. Remove it and reinsert at the position that minimizes crossings
   * 4. Repeat until no crossings remain or no progress is made
   *
   * Tries all NN orderings (start * side combinations) and returns the best valid result.
   */
  private fixNNByCrossingRelocation(prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>): PromptSideConnection[] | null {
    const n = prompts.length;
    if (n < 3) return null;

    let bestConnections: PromptSideConnection[] | null = null;
    let bestPerimeter = Infinity;
    const startTime = Date.now();
    const maxTimeMs = 1000;

    const countCrossings = (
      ord: number[],
    ): {
      count: number;
      crossingCounts: Map<number, number>;
    } => {
      const m = ord.length;
      const verts: THREE.Vector3[] = [];
      for (let k = 0; k < m; k++) {
        const data = intersections.get(`${ord[k]}-${ord[(k + 1) % m]}`);
        if (!data) return { count: Infinity, crossingCounts: new Map() };
        verts.push(data.point);
      }
      let count = 0;
      const crossingCounts = new Map<number, number>();
      for (let i = 0; i < m; i++) {
        for (let j = i + 2; j < m; j++) {
          if (i === 0 && j === m - 1) continue;
          if (this.segmentsIntersect(verts[i], verts[(i + 1) % m], verts[j], verts[(j + 1) % m])) {
            count++;
            for (const pos of [i, (i + 1) % m, j, (j + 1) % m]) {
              crossingCounts.set(pos, (crossingCounts.get(pos) ?? 0) + 1);
            }
          }
        }
      }
      return { count, crossingCounts };
    };

    // Count both geometric crossings AND side conflicts
    const countIssues = (
      ord: number[],
    ): {
      count: number;
      crossingCounts: Map<number, number>;
    } => {
      const m = ord.length;
      const base = countCrossings(ord);
      if (base.count === Infinity) return base;

      let count = base.count;
      const crossingCounts = new Map(base.crossingCounts);

      for (let p = 0; p < m; p++) {
        const prevData = intersections.get(`${ord[(p - 1 + m) % m]}-${ord[p]}`);
        const currData = intersections.get(`${ord[p]}-${ord[(p + 1) % m]}`);
        if (!prevData || !currData) continue;
        if (prevData.sideJ === currData.sideI) {
          count++;
          crossingCounts.set(p, (crossingCounts.get(p) ?? 0) + 1);
        }
      }

      return { count, crossingCounts };
    };

    // Collect unique NN orderings and sort by issue count (fewest first)
    const nnOrderings: Array<{ ordering: number[]; crossings: number }> = [];
    const seenOrderings = new Set<string>();
    for (let startIdx = 0; startIdx < n; startIdx++) {
      if (Date.now() - startTime > maxTimeMs) break;
      for (const side of ['pos', 'neg'] as const) {
        const ordering = this.buildNNOrderingWithSide(startIdx, side, n, intersections);
        if (!ordering || ordering.length !== n) continue;

        // Deduplicate cyclic orderings by normalizing (min element first)
        const minIdx = ordering.indexOf(Math.min(...ordering));
        const normalized = [...ordering.slice(minIdx), ...ordering.slice(0, minIdx)].join(',');
        if (seenOrderings.has(normalized)) continue;
        seenOrderings.add(normalized);

        const { count } = countIssues(ordering);
        if (count === 0) {
          // Already valid — try building
          const direct = this.tryOrdering(ordering, prompts, intersections);
          if (direct && direct.perimeter < bestPerimeter) {
            bestPerimeter = direct.perimeter;
            bestConnections = direct.connections;
          }
        } else if (count < Infinity) {
          nnOrderings.push({ ordering, crossings: count });
        }
      }
    }

    if (bestConnections) return bestConnections;

    // Sort by fewest crossings — most promising first
    nnOrderings.sort((a, b) => a.crossings - b.crossings);

    for (const { ordering } of nnOrderings) {
      if (bestConnections) break;
      if (Date.now() - startTime > maxTimeMs) break;

      // Has crossings — try iterative relocation
      let current = [...ordering];
      let prevCrossings = Infinity;
      let staleCount = 0;
      const maxRounds = n * 3;

      for (let round = 0; round < maxRounds; round++) {
        const { count: totalCrossings, crossingCounts } = countIssues(current);
        if (totalCrossings === 0) break;
        if (totalCrossings === Infinity) break;

        if (totalCrossings >= prevCrossings) {
          staleCount++;
          if (staleCount > crossingCounts.size) break;
        } else {
          staleCount = 0;
        }
        prevCrossings = totalCrossings;

        // Find the prompt with most crossing involvement
        // Ties broken by intersection count (more = less constrained = more likely wrong)
        let bestRemovePos = -1;
        let bestRemoveCrossings = 0;
        let bestRemoveIntCount = 0;

        for (const [pos, crossings] of crossingCounts) {
          const promptIdx = current[pos];
          let intCount = 0;
          for (let j = 0; j < n; j++) {
            if (promptIdx !== j && intersections.has(`${promptIdx}-${j}`)) intCount++;
          }

          if (crossings > bestRemoveCrossings || (crossings === bestRemoveCrossings && intCount > bestRemoveIntCount)) {
            bestRemoveCrossings = crossings;
            bestRemovePos = pos;
            bestRemoveIntCount = intCount;
          }
        }

        if (bestRemovePos === -1) break;

        const removed = current[bestRemovePos];
        const shorter = [...current];
        shorter.splice(bestRemovePos, 1);
        const m = shorter.length;

        let bestInsertPos = -1;
        let bestInsertCrossings = totalCrossings;

        for (let pos = 0; pos < m; pos++) {
          const prev = shorter[pos];
          const next = shorter[(pos + 1) % m];

          const e1 = intersections.get(`${prev}-${removed}`);
          const e2 = intersections.get(`${removed}-${next}`);
          if (!e1 || !e2) continue;
          if (e1.sideJ === e2.sideI) continue;

          const candidate = [...shorter];
          candidate.splice(pos + 1, 0, removed);

          const { count: newCrossings } = countIssues(candidate);
          if (newCrossings < bestInsertCrossings) {
            bestInsertCrossings = newCrossings;
            bestInsertPos = pos;
          }
        }

        if (bestInsertPos >= 0) {
          const candidate = [...shorter];
          candidate.splice(bestInsertPos + 1, 0, removed);
          current = candidate;
          continue;
        }

        // Single relocation didn't improve — try removing pairs of crossing prompts
        const crossingPositionsByCount = [...crossingCounts.entries()].sort((a, b) => b[1] - a[1]).map((e) => e[0]);

        let pairImproved = false;
        const pairLimit = Math.min(crossingPositionsByCount.length, 6);
        for (let pi = 0; pi < pairLimit && !pairImproved; pi++) {
          for (let pj = pi + 1; pj < pairLimit && !pairImproved; pj++) {
            const pos1 = crossingPositionsByCount[pi];
            const pos2 = crossingPositionsByCount[pj];
            const r1 = current[pos1];
            const r2 = current[pos2];

            const shorter2 = current.filter((_, idx) => idx !== pos1 && idx !== pos2);
            const m2 = shorter2.length;

            for (const [first, second] of [
              [r1, r2],
              [r2, r1],
            ] as const) {
              if (pairImproved) break;
              for (let ip1 = 0; ip1 < m2 && !pairImproved; ip1++) {
                const prev1 = shorter2[ip1];
                const next1 = shorter2[(ip1 + 1) % m2];
                const e1a = intersections.get(`${prev1}-${first}`);
                const e1b = intersections.get(`${first}-${next1}`);
                if (!e1a || !e1b) continue;
                if (e1a.sideJ === e1b.sideI) continue;

                const mid = [...shorter2];
                mid.splice(ip1 + 1, 0, first);
                const m3 = mid.length;

                for (let ip2 = 0; ip2 < m3 && !pairImproved; ip2++) {
                  const prev2 = mid[ip2];
                  const next2 = mid[(ip2 + 1) % m3];
                  const e2a = intersections.get(`${prev2}-${second}`);
                  const e2b = intersections.get(`${second}-${next2}`);
                  if (!e2a || !e2b) continue;
                  if (e2a.sideJ === e2b.sideI) continue;

                  const candidate2 = [...mid];
                  candidate2.splice(ip2 + 1, 0, second);

                  const { count: newCrossings } = countIssues(candidate2);
                  if (newCrossings < totalCrossings) {
                    current = candidate2;
                    pairImproved = true;
                  }
                }
              }
            }
          }
        }

        if (!pairImproved) break; // Neither single nor pair relocation helped
      }

      // Try as-is first
      let result = this.tryOrdering(current, prompts, intersections);
      if (result && result.perimeter < bestPerimeter) {
        bestPerimeter = result.perimeter;
        bestConnections = result.connections;
      }

      // If geometric crossings resolved but side conflicts remain, try segment reversals
      if (!result && countCrossings(current).count === 0) {
        let sideImproved = true;
        while (sideImproved) {
          sideImproved = false;
          const currentIssues = countIssues(current);
          if (currentIssues.count === 0) break;

          for (let len = 2; len <= Math.min(n - 1, 20) && !sideImproved; len++) {
            for (let start = 0; start < n && !sideImproved; start++) {
              const candidate = [...current];
              for (let x = 0; x < Math.floor(len / 2); x++) {
                const a = (start + x) % n;
                const b = (start + len - 1 - x) % n;
                [candidate[a], candidate[b]] = [candidate[b], candidate[a]];
              }
              const candIssues = countIssues(candidate);
              if (candIssues.count < currentIssues.count) {
                current = candidate;
                sideImproved = true;
              }
            }
          }
        }

        result = this.tryOrdering(current, prompts, intersections);
        if (result && result.perimeter < bestPerimeter) {
          bestPerimeter = result.perimeter;
          bestConnections = result.connections;
        }
      }
    }

    return bestConnections;
  }

  /**
   * Build a valid polygon ordering using DFS with crossing pruning.
   * At each step, only explores the K nearest side-compatible neighbors.
   * After adding each edge, checks if it crosses any existing edge — if so, prunes.
   * Tries multiple starting prompts.
   */
  private buildByDFSWithCrossingPruning(prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>, scanWindow = 8): PromptSideConnection[] | null {
    const n = prompts.length;
    if (n < 3) return null;

    let bestConnections: PromptSideConnection[] | null = null;
    let bestPerimeter = Infinity;
    let totalAttempts = 0;
    const maxAttempts = 100000;
    const startTime = Date.now();
    const maxTimeMs = 2000; // Hard time limit to prevent long searches

    // Pre-compute sorted neighbor lists for each (prompt, exitSide) combination
    const neighborCache = new Map<string, Array<{ idx: number; dist: number; entrySide: 'pos' | 'neg' }>>();
    for (let i = 0; i < n; i++) {
      for (const side of ['pos', 'neg'] as const) {
        const neighbors: Array<{ idx: number; dist: number; entrySide: 'pos' | 'neg' }> = [];
        for (let j = 0; j < n; j++) {
          if (j === i) continue;
          const key = `${i}-${j}`;
          const data = intersections.get(key);
          if (!data || data.sideI !== side) continue;
          neighbors.push({ idx: j, dist: data.distFromI + data.distFromJ, entrySide: data.sideJ });
        }
        neighbors.sort((a, b) => a.dist - b.dist);
        neighborCache.set(`${i}-${side}`, neighbors);
      }
    }

    const dfs = (ordering: number[], vertices: THREE.Vector3[], visited: Set<number>, lastPrompt: number, exitSide: 'pos' | 'neg', startPrompt: number, startEntrySideNeeded: 'pos' | 'neg'): void => {
      if (bestConnections) return;
      if (totalAttempts >= maxAttempts) return;
      totalAttempts++;

      // Periodic time check (every 256 calls) to prevent long searches
      if ((totalAttempts & 0xff) === 0 && Date.now() - startTime > maxTimeMs) return;

      if (ordering.length === n) {
        totalAttempts++;
        // Try to close the cycle
        const closeKey = `${lastPrompt}-${startPrompt}`;
        const closeData = intersections.get(closeKey);
        if (!closeData) return;
        if (closeData.sideI !== exitSide) return;
        if (closeData.sideJ !== startEntrySideNeeded) return;

        // Validate full ordering (checks simple polygon, side consistency, etc.)
        const result = this.tryOrdering(ordering, prompts, intersections);
        if (result && result.perimeter < bestPerimeter) {
          bestPerimeter = result.perimeter;
          bestConnections = result.connections;
        }
        return;
      }

      const neighbors = neighborCache.get(`${lastPrompt}-${exitSide}`);
      if (!neighbors) return;

      const limit = Math.min(scanWindow, neighbors.length);
      for (let i = 0; i < limit; i++) {
        if (bestConnections) return;
        if (totalAttempts >= maxAttempts) return;

        const next = neighbors[i];
        if (visited.has(next.idx)) continue;

        // Get the intersection point (new polygon vertex)
        const edgeKey = `${lastPrompt}-${next.idx}`;
        const edgeData = intersections.get(edgeKey);
        if (!edgeData) continue;
        const newPoint = edgeData.point;

        // Check if the new edge (from previous vertex to new vertex) crosses any existing edge.
        // The "new edge" is vertices[last] -> newPoint.
        // It should not cross any edge vertices[e] -> vertices[e+1] except the adjacent one.
        let crosses = false;
        if (vertices.length >= 2) {
          const prevPoint = vertices[vertices.length - 1];
          // Check against all edges except the last one (which shares prevPoint)
          for (let e = 0; e < vertices.length - 2; e++) {
            if (this.segmentsIntersect(prevPoint, newPoint, vertices[e], vertices[e + 1])) {
              crosses = true;
              break;
            }
          }
        }
        if (crosses) continue;

        visited.add(next.idx);
        ordering.push(next.idx);
        vertices.push(newPoint);
        const nextExitSide: 'pos' | 'neg' = next.entrySide === 'pos' ? 'neg' : 'pos';

        dfs(ordering, vertices, visited, next.idx, nextExitSide, startPrompt, startEntrySideNeeded);

        ordering.pop();
        vertices.pop();
        visited.delete(next.idx);
      }
    };

    // Order starting prompts by connectivity (most neighbors first)
    const startOrder = Array.from({ length: n }, (_, i) => {
      const posN = neighborCache.get(`${i}-pos`)?.length ?? 0;
      const negN = neighborCache.get(`${i}-neg`)?.length ?? 0;
      return { idx: i, connectivity: Math.min(posN, negN) };
    })
      .sort((a, b) => b.connectivity - a.connectivity)
      .map((s) => s.idx);

    for (const startIdx of startOrder) {
      if (bestConnections) break;
      if (totalAttempts >= maxAttempts || Date.now() - startTime > maxTimeMs) break;
      for (const startExitSide of ['pos', 'neg'] as const) {
        if (bestConnections) break;
        if (totalAttempts >= maxAttempts) break;

        const startEntrySideNeeded: 'pos' | 'neg' = startExitSide === 'pos' ? 'neg' : 'pos';
        const visited = new Set([startIdx]);

        dfs([startIdx], [], visited, startIdx, startExitSide, startIdx, startEntrySideNeeded);
      }
    }

    return bestConnections;
  }

  /**
   * Build a cyclic ordering starting from startIdx using a side-aware
   * nearest-neighbor heuristic.
   *
   * At each step, picks the unvisited prompt whose intersection with the current
   * prompt has the smallest combined distance AND is compatible with the side
   * constraint: each prompt must use opposite sides for incoming and outgoing edges.
   *
   * @param startSide - which side of the start prompt to exit from ('pos' or 'neg').
   *                    We try both and return the one that works.
   */
  private buildNearestNeighborOrdering(startIdx: number, n: number, intersections: Map<string, PromptIntersectionData>, startSide?: 'pos' | 'neg'): number[] | null {
    const sidesToTry: Array<'pos' | 'neg'> = startSide ? [startSide] : ['pos', 'neg'];

    for (const initialSide of sidesToTry) {
      const result = this.buildNNOrderingWithSide(startIdx, initialSide, n, intersections);
      if (result) return result;
    }

    return null;
  }

  private buildNNOrderingWithSide(startIdx: number, startExitSide: 'pos' | 'neg', n: number, intersections: Map<string, PromptIntersectionData>): number[] | null {
    const visited = new Set<number>();
    const ordering: number[] = [startIdx];
    visited.add(startIdx);

    let current = startIdx;
    let currentExitSide: 'pos' | 'neg' = startExitSide;

    for (let step = 1; step < n; step++) {
      let bestNext = -1;
      let bestDist = Infinity;
      let bestEntrySide: 'pos' | 'neg' = 'pos';

      for (let candidate = 0; candidate < n; candidate++) {
        if (visited.has(candidate)) continue;

        const key = `${current}-${candidate}`;
        const data = intersections.get(key);
        if (!data) continue;

        // The intersection must be on the current prompt's exit side
        if (data.sideI !== currentExitSide) continue;

        const combinedDist = data.distFromI + data.distFromJ;
        if (combinedDist < bestDist) {
          bestDist = combinedDist;
          bestNext = candidate;
          bestEntrySide = data.sideJ;
        }
      }

      if (bestNext === -1) return null; // Dead end

      ordering.push(bestNext);
      visited.add(bestNext);
      current = bestNext;
      // Exit from the opposite side of where we entered
      currentExitSide = bestEntrySide === 'pos' ? 'neg' : 'pos';
    }

    // Verify the closing edge exists and sides are compatible
    const closeKey = `${ordering[n - 1]}-${ordering[0]}`;
    const closeData = intersections.get(closeKey);
    if (!closeData) return null;

    // The closing edge must exit from currentExitSide of the last prompt
    if (closeData.sideI !== currentExitSide) return null;

    // And enter the start prompt on the opposite side of where it exits
    const startEntrySide = closeData.sideJ;
    if (startEntrySide === startExitSide) return null; // Same side — invalid

    return ordering;
  }

  /**
   * Fix a self-intersecting handshake polygon by identifying the crossing zone
   * and trying all permutations of just the involved prompts.
   *
   * Algorithm:
   * 1. Extract the traversal ordering from handshake connections
   * 2. Find all crossing edges
   * 3. Identify the contiguous zone of the chain that contains ALL crossing edges
   * 4. Keep the non-crossing prefix and suffix fixed
   * 5. Try all permutations of the prompts in the crossing zone (max ~12 prompts)
   * 6. For each permutation, rebuild the full ordering and validate
   */
  /**
   * Fix a self-intersecting polygon by identifying the crossing zone
   * and trying reorderings of just the involved prompts via side-aware DFS.
   * Accepts a raw ordering (number[]) directly.
   */
  private fixOrderingByCrossingReorder(ordering: number[], prompts: LinePrompt[], intersections: Map<string, PromptIntersectionData>): PromptSideConnection[] | null {
    const n = ordering.length;
    if (n < 3) return null;

    // Get vertices for crossing detection
    const vertices: THREE.Vector3[] = [];
    for (let k = 0; k < n; k++) {
      const key = `${ordering[k]}-${ordering[(k + 1) % n]}`;
      const data = intersections.get(key);
      if (!data) return null;
      vertices.push(data.point.clone());
    }

    // Find all crossing edge positions in the chain
    const crossingPositions = new Set<number>();
    for (let i = 0; i < n; i++) {
      for (let j = i + 2; j < n; j++) {
        if (i === 0 && j === n - 1) continue;
        if (this.segmentsIntersect(vertices[i], vertices[(i + 1) % n], vertices[j], vertices[(j + 1) % n])) {
          crossingPositions.add(i);
          crossingPositions.add((i + 1) % n);
          crossingPositions.add(j);
          crossingPositions.add((j + 1) % n);
        }
      }
    }

    if (crossingPositions.size === 0) return null; // No crossings (shouldn't happen)

    // Find the contiguous zone that spans all crossing positions
    const posArray = Array.from(crossingPositions).sort((a, b) => a - b);
    const zoneStart = posArray[0];
    const zoneEnd = posArray[posArray.length - 1];
    const zoneLength = zoneEnd - zoneStart + 1;

    // Cap at 20 prompts — use side-aware DFS with pruning
    if (zoneLength > 20) return null;

    // Extract the zone prompts and fixed prefix/suffix
    const prefix = ordering.slice(0, zoneStart);
    const zonePrompts = ordering.slice(zoneStart, zoneEnd + 1);
    const suffix = ordering.slice(zoneEnd + 1);

    let bestConnections: PromptSideConnection[] | null = null;
    let bestPerimeter = Infinity;

    // Determine the required exit side of the last prefix prompt
    // and the required entry side of the first suffix prompt.
    // The zone's first prompt must connect from the prefix's last prompt,
    // and the zone's last prompt must connect to the suffix's first prompt.

    // Side-aware DFS: tracks which side each prompt enters/exits
    const dfsReorder = (current: number[], remaining: Set<number>, lastPrompt: number, exitSide: 'pos' | 'neg'): void => {
      if (bestConnections) return; // Found a solution, stop searching

      if (remaining.size === 0) {
        const fullOrdering = [...prefix, ...current, ...suffix];
        const result = this.tryOrdering(fullOrdering, prompts, intersections);
        if (result && result.perimeter < bestPerimeter) {
          bestPerimeter = result.perimeter;
          bestConnections = result.connections;
        }
        return;
      }

      // Find candidates with valid intersections on the correct exit side
      const candidates: Array<{ idx: number; dist: number; entrySide: 'pos' | 'neg' }> = [];
      for (const candidate of remaining) {
        const key = `${lastPrompt}-${candidate}`;
        const data = intersections.get(key);
        if (!data) continue;
        if (data.sideI !== exitSide) continue;
        candidates.push({ idx: candidate, dist: data.distFromI + data.distFromJ, entrySide: data.sideJ });
      }
      candidates.sort((a, b) => a.dist - b.dist);

      const limit = Math.min(6, candidates.length);
      for (let i = 0; i < limit; i++) {
        const next = candidates[i];
        remaining.delete(next.idx);
        current.push(next.idx);
        const nextExitSide: 'pos' | 'neg' = next.entrySide === 'pos' ? 'neg' : 'pos';
        dfsReorder(current, remaining, next.idx, nextExitSide);
        current.pop();
        remaining.add(next.idx);
      }
    };

    // Determine exit side from the prefix's last prompt (or try both if no prefix)
    const sidesToTry: Array<'pos' | 'neg'> = ['pos', 'neg'];

    for (const startPrompt of zonePrompts) {
      if (bestConnections) break;
      for (const startExitSide of sidesToTry) {
        if (bestConnections) break;
        const remaining = new Set(zonePrompts);
        remaining.delete(startPrompt);
        dfsReorder([startPrompt], remaining, startPrompt, startExitSide);
      }
    }

    return bestConnections;
  }

  /**
   * Detect and fix self-intersecting connections by swapping the connections
   * at the crossing points. When two connections cross, we swap which prompts
   * they connect to, ensuring the shape doesn't cross itself.
   */
  private fixSelfIntersections(connections: PromptSideConnection[], prompts: LinePrompt[]): PromptSideConnection[] {
    if (connections.length < 2) return connections;

    const fixedConnections = [...connections];
    let foundIntersection = true;
    const maxIterations = 10; // Prevent infinite loops
    let iteration = 0;

    while (foundIntersection && iteration < maxIterations) {
      foundIntersection = false;
      iteration++;

      // Check all pairs of connections for intersections
      for (let i = 0; i < fixedConnections.length; i++) {
        for (let j = i + 1; j < fixedConnections.length; j++) {
          const conn1 = fixedConnections[i];
          const conn2 = fixedConnections[j];

          // Get the connection edges (from prompt points to connection intersection points)
          // For conn1: we have two edges from promptA and promptB to their shared connection point
          // For conn2: we have two edges from promptA and promptB to their shared connection point
          // We need to check if any of these edges cross each other

          // Connection 1 edges: promptA -> connection point, promptB -> connection point
          const conn1EdgeA = { start: prompts[conn1.promptA].worldPoint, end: conn1.point };
          const conn1EdgeB = { start: prompts[conn1.promptB].worldPoint, end: conn1.point };

          // Connection 2 edges: promptA -> connection point, promptB -> connection point
          const conn2EdgeA = { start: prompts[conn2.promptA].worldPoint, end: conn2.point };
          const conn2EdgeB = { start: prompts[conn2.promptB].worldPoint, end: conn2.point };

          // Check all combinations of edges for intersections
          const intersections = [
            this.segmentsIntersect(conn1EdgeA.start, conn1EdgeA.end, conn2EdgeA.start, conn2EdgeA.end),
            this.segmentsIntersect(conn1EdgeA.start, conn1EdgeA.end, conn2EdgeB.start, conn2EdgeB.end),
            this.segmentsIntersect(conn1EdgeB.start, conn1EdgeB.end, conn2EdgeA.start, conn2EdgeA.end),
            this.segmentsIntersect(conn1EdgeB.start, conn1EdgeB.end, conn2EdgeB.start, conn2EdgeB.end),
          ].filter(Boolean);

          const intersection = intersections.length > 0 ? intersections[0] : null;

          if (intersection) {
            // Found a crossing! We need to swap the connections.
            // The four prompts involved are: conn1.promptA, conn1.promptB, conn2.promptA, conn2.promptB
            // Each with their respective sides: conn1.sideA, conn1.sideB, conn2.sideA, conn2.sideB

            // We need to reconfigure the connections to avoid the crossing.
            // Current state: (conn1.promptA, conn1.sideA) <-> (conn1.promptB, conn1.sideB)
            //                (conn2.promptA, conn2.sideA) <-> (conn2.promptB, conn2.sideB)
            // These two connections cross each other.

            // Try swapping the B endpoints while keeping the sides intact
            // Option 1: (conn1.promptA, conn1.sideA) <-> (conn2.promptA, conn2.sideA)
            //           (conn1.promptB, conn1.sideB) <-> (conn2.promptB, conn2.sideB)
            const swapOption1 = this.evaluateSwap(conn1.promptA, conn2.promptA, conn1.sideA, conn2.sideA, conn1.promptB, conn2.promptB, conn1.sideB, conn2.sideB, prompts);

            // Option 2: (conn1.promptA, conn1.sideA) <-> (conn2.promptB, conn2.sideB)
            //           (conn1.promptB, conn1.sideB) <-> (conn2.promptA, conn2.sideA)
            const swapOption2 = this.evaluateSwap(conn1.promptA, conn2.promptB, conn1.sideA, conn2.sideB, conn1.promptB, conn2.promptA, conn1.sideB, conn2.sideA, prompts);

            // Choose the swap with shorter total distance and valid configuration
            let newConn1, newConn2;
            if (swapOption1.totalDistance < swapOption2.totalDistance && swapOption1.totalDistance !== Infinity) {
              newConn1 = {
                promptA: conn1.promptA,
                promptB: conn2.promptA,
                sideA: conn1.sideA,
                sideB: conn2.sideA,
                point: swapOption1.point1,
              };
              newConn2 = {
                promptA: conn1.promptB,
                promptB: conn2.promptB,
                sideA: conn1.sideB,
                sideB: conn2.sideB,
                point: swapOption1.point2,
              };
            } else if (swapOption2.totalDistance !== Infinity) {
              newConn1 = {
                promptA: conn1.promptA,
                promptB: conn2.promptB,
                sideA: conn1.sideA,
                sideB: conn2.sideB,
                point: swapOption2.point1,
              };
              newConn2 = {
                promptA: conn1.promptB,
                promptB: conn2.promptA,
                sideA: conn1.sideB,
                sideB: conn2.sideA,
                point: swapOption2.point2,
              };
            } else {
              // Neither swap is valid, skip this intersection
              continue;
            }

            // Replace the connections
            fixedConnections[i] = newConn1;
            fixedConnections[j] = newConn2;

            foundIntersection = true;
            break;
          }
        }
        if (foundIntersection) break;
      }
    }

    return fixedConnections;
  }

  /**
   * Evaluate a potential swap configuration by calculating new intersection points
   * and total distance. Returns the total distance and new intersection points.
   * Takes into account which side of each prompt should connect.
   */
  private evaluateSwap(
    promptA1: number,
    promptB1: number,
    sideA1: 'pos' | 'neg',
    sideB1: 'pos' | 'neg',
    promptA2: number,
    promptB2: number,
    sideA2: 'pos' | 'neg',
    sideB2: 'pos' | 'neg',
    prompts: LinePrompt[],
  ): { totalDistance: number; point1: THREE.Vector3; point2: THREE.Vector3 } {
    // Calculate intersection point for first connection
    const point1 = this.findInfiniteLineIntersection(prompts[promptA1].worldPoint, prompts[promptA1].direction, prompts[promptB1].worldPoint, prompts[promptB1].direction);

    // Calculate intersection point for second connection
    const point2 = this.findInfiniteLineIntersection(prompts[promptA2].worldPoint, prompts[promptA2].direction, prompts[promptB2].worldPoint, prompts[promptB2].direction);

    if (!point1 || !point2) {
      return { totalDistance: Infinity, point1: new THREE.Vector3(), point2: new THREE.Vector3() };
    }

    // Verify that the intersection points are on the correct sides
    const vecFromA1 = new THREE.Vector3().subVectors(point1, prompts[promptA1].worldPoint);
    const dotA1 = vecFromA1.dot(prompts[promptA1].direction);
    const actualSideA1: 'pos' | 'neg' = dotA1 >= 0 ? 'pos' : 'neg';

    const vecFromB1 = new THREE.Vector3().subVectors(point1, prompts[promptB1].worldPoint);
    const dotB1 = vecFromB1.dot(prompts[promptB1].direction);
    const actualSideB1: 'pos' | 'neg' = dotB1 >= 0 ? 'pos' : 'neg';

    const vecFromA2 = new THREE.Vector3().subVectors(point2, prompts[promptA2].worldPoint);
    const dotA2 = vecFromA2.dot(prompts[promptA2].direction);
    const actualSideA2: 'pos' | 'neg' = dotA2 >= 0 ? 'pos' : 'neg';

    const vecFromB2 = new THREE.Vector3().subVectors(point2, prompts[promptB2].worldPoint);
    const dotB2 = vecFromB2.dot(prompts[promptB2].direction);
    const actualSideB2: 'pos' | 'neg' = dotB2 >= 0 ? 'pos' : 'neg';

    // If the sides don't match, this swap is invalid
    if (actualSideA1 !== sideA1 || actualSideB1 !== sideB1 || actualSideA2 !== sideA2 || actualSideB2 !== sideB2) {
      return { totalDistance: Infinity, point1: new THREE.Vector3(), point2: new THREE.Vector3() };
    }

    // Calculate total distance from prompts to intersection points
    const dist1A = prompts[promptA1].worldPoint.distanceTo(point1);
    const dist1B = prompts[promptB1].worldPoint.distanceTo(point1);
    const dist2A = prompts[promptA2].worldPoint.distanceTo(point2);
    const dist2B = prompts[promptB2].worldPoint.distanceTo(point2);

    const totalDistance = dist1A + dist1B + dist2A + dist2B;

    return { totalDistance, point1, point2 };
  }

  /**
   * Find all mutual connections between prompts using a true handshake algorithm.
   *
   * Algorithm:
   * 1. For each unlocked prompt side, find its closest unlocked target
   * 2. Check if that target's closest unlocked option is also pointing back (mutual)
   * 3. If mutual from BOTH perspectives → confirmed connection, lock both sides
   * 4. Repeat until no more mutual connections found
   * 5. Fallback: connect remaining unlocked sides using shortest available edges
   * 6. After all connections are made, detect and fix any self-intersections
   *
   * Key insight: A connection is only valid if BOTH sides consider each other
   * their shortest available option. This ensures true mutual "handshake".
   */
  private findMutualConnections(prompts: LinePrompt[], precomputedIntersections?: Map<string, PromptIntersectionData>): PromptSideConnection[] {
    const n = prompts.length;
    const connections: PromptSideConnection[] = [];

    // Track which sides of each prompt are already locked
    const lockedSides = new Set<string>();

    const isLocked = (promptIdx: number, side: 'pos' | 'neg'): boolean => {
      return lockedSides.has(`${promptIdx}-${side}`);
    };

    const lockSide = (promptIdx: number, side: 'pos' | 'neg'): void => {
      lockedSides.add(`${promptIdx}-${side}`);
    };

    // Use pre-computed intersections or compute them
    const intersections = precomputedIntersections ?? this.computeAllIntersections(prompts);

    /**
     * Find the closest unlocked target for a given prompt side.
     * Returns null if no unlocked targets available.
     * @param useCombinedDistance - if true, uses distFromI + distFromJ instead of just distFromI
     */
    const findClosestUnlockedTarget = (fromPrompt: number, fromSide: 'pos' | 'neg', useCombinedDistance = false): { targetPrompt: number; targetSide: 'pos' | 'neg'; distance: number; point: THREE.Vector3 } | null => {
      let closest: { targetPrompt: number; targetSide: 'pos' | 'neg'; distance: number; point: THREE.Vector3 } | null = null;

      for (let j = 0; j < n; j++) {
        if (j === fromPrompt) continue;

        const key = `${fromPrompt}-${j}`;
        const data = intersections.get(key);
        if (!data) continue;

        // Check if this intersection is on the correct side of fromPrompt
        if (data.sideI !== fromSide) continue;

        // Check if the target side is locked
        if (isLocked(j, data.sideJ)) continue;

        // Use combined distance or just source distance
        const dist = useCombinedDistance ? data.distFromI + data.distFromJ : data.distFromI;

        if (!closest || dist < closest.distance) {
          closest = {
            targetPrompt: j,
            targetSide: data.sideJ,
            distance: dist,
            point: data.point.clone(),
          };
        }
      }

      return closest;
    };

    /**
     * Run mutual handshake algorithm to find connections.
     * @param useCombinedDistance - if true, uses distFromI + distFromJ for distance calculation
     */
    const runMutualHandshake = (useCombinedDistance = false): void => {
      let foundConnection = true;
      while (foundConnection) {
        foundConnection = false;

        // For each unlocked side, find its closest target and check for mutual handshake
        for (let i = 0; i < n; i++) {
          for (const sideI of ['pos', 'neg'] as const) {
            if (isLocked(i, sideI)) continue;

            // Find closest unlocked target from i's perspective
            const closestFromI = findClosestUnlockedTarget(i, sideI, useCombinedDistance);
            if (!closestFromI) continue;

            const { targetPrompt: j, targetSide: sideJ } = closestFromI;

            // Now check from j's perspective: is i also j's closest option?
            const closestFromJ = findClosestUnlockedTarget(j, sideJ, useCombinedDistance);
            if (!closestFromJ) continue;

            // MUTUAL HANDSHAKE: both must point to each other as their shortest option
            if (closestFromJ.targetPrompt === i && closestFromJ.targetSide === sideI) {
              // Confirmed mutual connection!
              lockSide(i, sideI);
              lockSide(j, sideJ);

              connections.push({
                promptA: i,
                promptB: j,
                sideA: sideI,
                sideB: sideJ,
                point: closestFromI.point.clone(),
              });

              foundConnection = true;
              break; // Restart the search since state changed
            }
          }

          if (foundConnection) break; // Restart outer loop
        }
      }
    };

    // Check if all prompt sides are connected
    const allSidesConnected = (): boolean => {
      for (let i = 0; i < n; i++) {
        if (!isLocked(i, 'pos') || !isLocked(i, 'neg')) {
          return false;
        }
      }
      return true;
    };

    // Phase 1: Find all mutual handshake connections using distFromI
    runMutualHandshake(false);

    // Phase 1.5: If not all connections found, clear and retry with combined distance
    if (!allSidesConnected()) {
      lockedSides.clear();
      connections.length = 0;
      runMutualHandshake(true);
    }

    // Phase 2: Fallback - connect remaining unlocked sides using shortest available edges
    // This handles cases where strict mutual handshake creates deadlocks
    // Collect all remaining valid edges and sort by max of both distances (ensures both sides are short)
    const remainingEdges: Array<{
      i: number;
      j: number;
      sideI: 'pos' | 'neg';
      sideJ: 'pos' | 'neg';
      point: THREE.Vector3;
      maxDist: number;
    }> = [];

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const key = `${i}-${j}`;
        const data = intersections.get(key);
        if (!data) continue;

        // Only consider if both sides are still unlocked
        if (isLocked(i, data.sideI) || isLocked(j, data.sideJ)) continue;

        // Use max distance to ensure BOTH sides are reasonably short
        const maxDist = Math.max(data.distFromI, data.distFromJ);

        remainingEdges.push({
          i,
          j,
          sideI: data.sideI,
          sideJ: data.sideJ,
          point: data.point.clone(),
          maxDist,
        });
      }
    }

    // Sort by max distance (shortest first) - this ensures both sides are short
    remainingEdges.sort((a, b) => a.maxDist - b.maxDist);

    // Greedily add remaining edges
    for (const edge of remainingEdges) {
      const { i, j, sideI, sideJ, point } = edge;

      // Re-check if both sides are still available (may have been locked in this phase)
      if (isLocked(i, sideI) || isLocked(j, sideJ)) continue;

      lockSide(i, sideI);
      lockSide(j, sideJ);

      connections.push({
        promptA: i,
        promptB: j,
        sideA: sideI,
        sideB: sideJ,
        point: point.clone(),
      });
    }

    // After all connections are made, detect and fix self-intersections
    const fixedConnections = this.fixSelfIntersections(connections, prompts);

    return fixedConnections;
  }

  /**
   * Compute all pairwise intersections between prompts
   */
  private computeAllIntersections(prompts: LinePrompt[]): Map<string, PromptIntersectionData> {
    const intersections = new Map<string, PromptIntersectionData>();
    const n = prompts.length;

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const intersection = this.findInfiniteLineIntersection(prompts[i].worldPoint, prompts[i].direction, prompts[j].worldPoint, prompts[j].direction);

        if (intersection) {
          const vecFromI = new THREE.Vector3().subVectors(intersection, prompts[i].worldPoint);
          const vecFromJ = new THREE.Vector3().subVectors(intersection, prompts[j].worldPoint);

          const dotI = vecFromI.dot(prompts[i].direction);
          const dotJ = vecFromJ.dot(prompts[j].direction);

          const distFromI = prompts[i].worldPoint.distanceTo(intersection);
          const distFromJ = prompts[j].worldPoint.distanceTo(intersection);

          const sideI: 'pos' | 'neg' = dotI >= 0 ? 'pos' : 'neg';
          const sideJ: 'pos' | 'neg' = dotJ >= 0 ? 'pos' : 'neg';

          // Store for both directions
          intersections.set(`${i}-${j}`, { point: intersection.clone(), distFromI, distFromJ, sideI, sideJ });
          intersections.set(`${j}-${i}`, {
            point: intersection.clone(),
            distFromI: distFromJ,
            distFromJ: distFromI,
            sideI: sideJ,
            sideJ: sideI,
          });
        }
      }
    }

    return intersections;
  }

  /**
   * Build a polygon path from the confirmed connections.
   * Follows the chain of connections starting from the first prompt.
   */
  private buildPolygonFromConnections(connections: PromptSideConnection[], prompts: LinePrompt[]): THREE.Vector3[] | null {
    if (connections.length < 3) return null;

    // Build adjacency: for each prompt+side, what connection does it lead to?
    const adjacency = new Map<string, { nextPrompt: number; nextSide: 'pos' | 'neg'; point: THREE.Vector3 }>();

    for (const conn of connections) {
      // From promptA's sideA, we go to promptB (entering at sideB)
      // The "next side" to exit from promptB is the opposite of sideB
      const exitSideB = conn.sideB === 'pos' ? 'neg' : 'pos';
      adjacency.set(`${conn.promptA}-${conn.sideA}`, {
        nextPrompt: conn.promptB,
        nextSide: exitSideB,
        point: conn.point,
      });

      // Reverse direction
      const exitSideA = conn.sideA === 'pos' ? 'neg' : 'pos';
      adjacency.set(`${conn.promptB}-${conn.sideB}`, {
        nextPrompt: conn.promptA,
        nextSide: exitSideA,
        point: conn.point,
      });
    }

    // Start from the first connection's first prompt
    const startPrompt = connections[0].promptA;
    const startSide = connections[0].sideA;

    const path: THREE.Vector3[] = [];
    const visited = new Set<string>();

    let currentPrompt = startPrompt;
    let currentSide = startSide;

    // Follow the chain
    for (let step = 0; step < prompts.length * 2; step++) {
      const key = `${currentPrompt}-${currentSide}`;

      if (visited.has(key)) {
        // We've completed the loop
        break;
      }

      visited.add(key);

      const next = adjacency.get(key);
      if (!next) {
        // Dead end - incomplete polygon
        break;
      }

      path.push(next.point.clone());
      currentPrompt = next.nextPrompt;
      currentSide = next.nextSide;
    }

    return path.length >= 3 ? path : null;
  }

  /**
   * Find intersection point of two infinite lines defined by point + direction
   */
  private findInfiniteLineIntersection(p1: THREE.Vector3, d1: THREE.Vector3, p2: THREE.Vector3, d2: THREE.Vector3): THREE.Vector3 | null {
    // Project to 2D (X-Y plane)
    const x1 = p1.x,
      y1 = p1.y;
    const dx1 = d1.x,
      dy1 = d1.y;
    const x2 = p2.x,
      y2 = p2.y;
    const dx2 = d2.x,
      dy2 = d2.y;

    // Solve: p1 + t*d1 = p2 + s*d2
    // x1 + t*dx1 = x2 + s*dx2
    // y1 + t*dy1 = y2 + s*dy2

    const denom = dx1 * dy2 - dy1 * dx2;

    if (Math.abs(denom) < 0.0001) {
      return null; // Parallel lines
    }

    const t = ((x2 - x1) * dy2 - (y2 - y1) * dx2) / denom;

    // Calculate intersection point
    const x = x1 + t * dx1;
    const y = y1 + t * dy1;
    const z = p1.z + t * (d1.z || 0); // Interpolate Z if available

    return new THREE.Vector3(x, y, z);
  }

  /**
   * Check if all prompts have both sides connected.
   * For a valid closed shape, each prompt needs exactly 2 connections (one on each side).
   */
  private checkAllPromptsConnected(promptCount: number, connections: PromptSideConnection[]): boolean {
    // Track connection count for each prompt side
    const connectionCounts = new Map<string, number>();

    for (const conn of connections) {
      const keyA = `${conn.promptA}-${conn.sideA}`;
      const keyB = `${conn.promptB}-${conn.sideB}`;

      connectionCounts.set(keyA, (connectionCounts.get(keyA) || 0) + 1);
      connectionCounts.set(keyB, (connectionCounts.get(keyB) || 0) + 1);
    }

    // Check that every prompt has exactly one connection on each side
    for (let i = 0; i < promptCount; i++) {
      const posKey = `${i}-pos`;
      const negKey = `${i}-neg`;

      const posCount = connectionCounts.get(posKey) || 0;
      const negCount = connectionCounts.get(negKey) || 0;

      if (posCount !== 1 || negCount !== 1) {
        return false;
      }
    }

    // Verify all connections form a single cycle (not multiple disjoint cycles)
    if (connections.length > 0) {
      const adjacency = new Map<string, { nextPrompt: number; nextSide: 'pos' | 'neg' }>();
      for (const conn of connections) {
        const exitSideB: 'pos' | 'neg' = conn.sideB === 'pos' ? 'neg' : 'pos';
        adjacency.set(`${conn.promptA}-${conn.sideA}`, { nextPrompt: conn.promptB, nextSide: exitSideB });
        const exitSideA: 'pos' | 'neg' = conn.sideA === 'pos' ? 'neg' : 'pos';
        adjacency.set(`${conn.promptB}-${conn.sideB}`, { nextPrompt: conn.promptA, nextSide: exitSideA });
      }

      const visited = new Set<number>();
      let current = connections[0].promptA;
      let side = connections[0].sideA;

      for (let step = 0; step < promptCount * 2; step++) {
        const key = `${current}-${side}`;
        const next = adjacency.get(key);
        if (!next) return false;
        visited.add(current);
        current = next.nextPrompt;
        side = next.nextSide;
        if (current === connections[0].promptA && side === connections[0].sideA) break;
      }

      if (visited.size !== promptCount) return false;
    }

    return true;
  }

  /**
   * Update prompts with their connections based on found connections.
   * This allows the visualization to show only the connected segments
   * and provides information about which prompts are connected.
   * Also updates positiveLength and negativeLength to match the connection points.
   */
  private updatePromptConnections(prompts: LinePrompt[], connections: PromptSideConnection[]): void {
    // Clear existing connections and lengths
    for (const prompt of prompts) {
      prompt.connections = {
        positive: undefined,
        negative: undefined,
      };
      prompt.positiveLength = undefined;
      prompt.negativeLength = undefined;
    }

    // Set connections from found connections
    for (const conn of connections) {
      const connectionPoint = conn.point.clone();

      // Set connection for promptA
      const connectionsA = prompts[conn.promptA].connections;
      if (connectionsA) {
        if (conn.sideA === 'pos') {
          connectionsA.positive = {
            targetPromptIndex: conn.promptB,
            targetSide: conn.sideB,
            point: connectionPoint,
          };
        } else {
          connectionsA.negative = {
            targetPromptIndex: conn.promptB,
            targetSide: conn.sideB,
            point: connectionPoint,
          };
        }
      }

      // Set connection for promptB
      const connectionsB = prompts[conn.promptB].connections;
      if (connectionsB) {
        if (conn.sideB === 'pos') {
          connectionsB.positive = {
            targetPromptIndex: conn.promptA,
            targetSide: conn.sideA,
            point: connectionPoint,
          };
        } else {
          connectionsB.negative = {
            targetPromptIndex: conn.promptA,
            targetSide: conn.sideA,
            point: connectionPoint,
          };
        }
      }
    }

    // Compute lengths from worldPoint to connection points
    // Set each side independently so partially-connected prompts (e.g. temporary
    // prompts with one internal and one external connection) still get correct lengths
    for (const prompt of prompts) {
      const posConn = prompt.connections?.positive;
      if (posConn?.point) {
        posConn.point.z = prompt.worldPoint.z;
        prompt.positiveLength = prompt.worldPoint.distanceTo(posConn.point);
      }
      const negConn = prompt.connections?.negative;
      if (negConn?.point) {
        negConn.point.z = prompt.worldPoint.z;
        prompt.negativeLength = prompt.worldPoint.distanceTo(negConn.point);
      }
    }

    // Disconnect sides where the connection point is unreasonably far
    // (near-parallel lines intersecting at near-infinity)
    this.disconnectFarConnections(prompts);
  }

  /**
   * Remove connections where the length is unreasonably far from the prompt,
   * based on 3x the average pairwise distance between all prompts.
   */
  private disconnectFarConnections(prompts: LinePrompt[]): void {
    const n = prompts.length;
    if (n < 2) return;

    let totalDist = 0;
    let count = 0;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        totalDist += prompts[i].worldPoint.distanceTo(prompts[j].worldPoint);
        count++;
      }
    }
    const maxDist = (totalDist / count) * 3;

    for (const prompt of prompts) {
      if (prompt.positiveLength !== undefined && prompt.positiveLength > maxDist) {
        prompt.positiveLength = undefined;
        if (prompt.connections) prompt.connections.positive = undefined;
      }
      if (prompt.negativeLength !== undefined && prompt.negativeLength > maxDist) {
        prompt.negativeLength = undefined;
        if (prompt.connections) prompt.connections.negative = undefined;
      }
    }
  }

  /**
   * Create shape from prompts that can connect to an external polygon.
   * Used for temporary prompts that extend an existing shape.
   *
   * This works exactly like createShapeFromPrompts but also creates "virtual prompts"
   * from the external polygon edges so temporary prompts can connect to them.
   *
   * @param prompts - The temporary prompts to build shape from
   * @param externalPolygon - Vertices of the existing polygon to connect to
   * @returns Shape info and external connection points, or null if invalid
   */
  createShapeWithExternalPolygon(
    prompts: LinePrompt[],
    externalPolygon: THREE.Vector3[],
  ): {
    shape: LineShape | null;
    connections: ExternalConnection[];
  } | null {
    const t = this.coordinateTransformer;
    if (!t || !t.getBasePlaneFrame()) return this.createShapeWithExternalPolygonCore(prompts, externalPolygon);

    // Snapshot prompts in world, mutate to local, also map external
    // polygon to local for the duration of the call. On exit: restore
    // prompts, map output shape vertices/center + connection points
    // back to world.
    const snapshots = prompts.map((p) => ({ worldPoint: p.worldPoint.clone(), direction: p.direction.clone() }));
    for (const p of prompts) {
      p.worldPoint.copy(t.toBasePlaneLocal(p.worldPoint));
      p.direction.copy(t.toBasePlaneLocalDir(p.direction));
    }
    const localExternal = externalPolygon.map((v) => t.toBasePlaneLocal(v));

    const result = this.createShapeWithExternalPolygonCore(prompts, localExternal);

    // Map prompt connection points local→world before restoring prompts.
    // `updatePromptConnections` shares the same Vector3 reference between
    // both endpoints of an intersection, so dedupe by identity to avoid
    // double-mapping.
    const visitedPoints = new Set<THREE.Vector3>();
    for (const p of prompts) {
      const pos = p.connections?.positive;
      const neg = p.connections?.negative;
      if (pos?.point && !visitedPoints.has(pos.point)) {
        visitedPoints.add(pos.point);
        pos.point.copy(t.fromBasePlaneLocal(pos.point));
      }
      if (neg?.point && !visitedPoints.has(neg.point)) {
        visitedPoints.add(neg.point);
        neg.point.copy(t.fromBasePlaneLocal(neg.point));
      }
    }
    for (let i = 0; i < prompts.length; i++) {
      prompts[i].worldPoint.copy(snapshots[i].worldPoint);
      prompts[i].direction.copy(snapshots[i].direction);
    }

    if (result) {
      if (result.shape) {
        for (const v of result.shape.vertices) v.copy(t.fromBasePlaneLocal(v));
        if (result.shape.center) result.shape.center.copy(t.fromBasePlaneLocal(result.shape.center));
      }
      for (const c of result.connections) {
        if (c.point) c.point.copy(t.fromBasePlaneLocal(c.point));
      }
    }
    return result;
  }

  private createShapeWithExternalPolygonCore(
    prompts: LinePrompt[],
    externalPolygon: THREE.Vector3[],
  ): {
    shape: LineShape | null;
    connections: ExternalConnection[];
  } | null {
    if (prompts.length === 0 || externalPolygon.length < 3) {
      return null;
    }

    // Step 1: Find internal connections between temp prompts first
    // But only accept intersections that are CLOSER than the external polygon
    // (i.e., the intersection point must be between the prompt and the polygon)
    let internalConnections: PromptSideConnection[] = [];
    if (prompts.length >= 2) {
      const internalIntersections = this.computeAllIntersections(prompts);
      const allInternalConnections = this.findMutualConnections(prompts, internalIntersections);

      // Filter: only keep internal connections where the intersection is closer
      // than the external polygon edge for BOTH prompt sides
      internalConnections = allInternalConnections.filter((conn) => {
        const promptA = prompts[conn.promptA];
        const promptB = prompts[conn.promptB];

        // Get distance to external polygon for each side
        const extA = this.findBestExternalConnection(promptA, conn.sideA, externalPolygon);
        const extB = this.findBestExternalConnection(promptB, conn.sideB, externalPolygon);

        // Distance from prompt to intersection point
        const distA = promptA.worldPoint.distanceTo(conn.point);
        const distB = promptB.worldPoint.distanceTo(conn.point);

        // Internal connection is valid only if the intersection is closer than
        // the external polygon for BOTH prompts (or if there's no external connection)
        const aCloser = !extA || distA < extA.distance;
        const bCloser = !extB || distB < extB.distance;

        return aCloser && bCloser;
      });
    }

    // Track which sides are used by internal connections
    const usedSides = new Set<string>();
    for (const conn of internalConnections) {
      usedSides.add(`${conn.promptA}-${conn.sideA}`);
      usedSides.add(`${conn.promptB}-${conn.sideB}`);
    }

    // Step 2: For free sides (not used by internal connections),
    // find connections to the external polygon
    const externalConnections: ExternalConnection[] = [];

    for (let promptIdx = 0; promptIdx < prompts.length; promptIdx++) {
      const prompt = prompts[promptIdx];

      for (const side of ['pos', 'neg'] as const) {
        const sideKey = `${promptIdx}-${side}`;
        if (usedSides.has(sideKey)) continue; // Already connected internally

        const bestExternal = this.findBestExternalConnection(prompt, side, externalPolygon);
        if (bestExternal) {
          externalConnections.push({
            promptIndex: promptIdx,
            promptSide: side,
            edgeIndex: bestExternal.edgeIndex,
            point: bestExternal.point.clone(),
          });
        }
      }
    }

    // Step 3: Update the real prompts' connections for visualization
    this.updatePromptConnections(prompts, internalConnections);

    // Also set external connection points on the real prompts
    for (const extConn of externalConnections) {
      const prompt = prompts[extConn.promptIndex];
      if (!prompt.connections) {
        prompt.connections = {};
      }
      if (extConn.promptSide === 'pos') {
        prompt.connections.positive = {
          targetPromptIndex: -1, // Special marker for external
          targetSide: 'pos',
          point: extConn.point.clone(),
        };
        prompt.positiveLength = prompt.worldPoint.distanceTo(extConn.point);
      } else {
        prompt.connections.negative = {
          targetPromptIndex: -1, // Special marker for external
          targetSide: 'neg',
          point: extConn.point.clone(),
        };
        prompt.negativeLength = prompt.worldPoint.distanceTo(extConn.point);
      }
    }

    // For a valid extension, we need exactly 2 external connections
    if (externalConnections.length !== 2) {
      return { shape: null, connections: externalConnections };
    }

    // Build the shape vertices by tracing the path
    const vertices: THREE.Vector3[] = [];
    const [conn1, conn2] = externalConnections;

    // Start with the first external connection point
    vertices.push(conn1.point.clone());

    // If we have internal connections, build path through them
    if (internalConnections.length > 0) {
      const pathPoints = this.buildPathBetweenExternalConnections(conn1, conn2, internalConnections, prompts);

      if (pathPoints) {
        vertices.push(...pathPoints);
      }
    }

    // End with the second external connection point
    vertices.push(conn2.point.clone());

    if (vertices.length < 2) {
      return { shape: null, connections: externalConnections };
    }

    const center = this.calculateCenter(vertices);

    return {
      shape: {
        id: `temp_shape_${this.nextShapeId++}`,
        vertices,
        boundaryLines: [],
        isClosed: false,
        center,
      },
      connections: externalConnections,
    };
  }

  /**
   * Find the best connection from a prompt side to an external polygon edge.
   * Shoots a ray from the prompt in the given direction and finds the closest
   * intersection with any polygon edge.
   */
  private findBestExternalConnection(prompt: LinePrompt, side: 'pos' | 'neg', externalPolygon: THREE.Vector3[]): { edgeIndex: number; point: THREE.Vector3; distance: number } | null {
    // Direction to shoot the ray (positive or negative along prompt direction)
    const rayDirection = side === 'pos' ? prompt.direction.clone() : prompt.direction.clone().negate();

    let bestResult: { edgeIndex: number; point: THREE.Vector3; distance: number } | null = null;

    // Check intersection with each edge of the external polygon
    for (let i = 0; i < externalPolygon.length; i++) {
      const edgeStart = externalPolygon[i];
      const edgeEnd = externalPolygon[(i + 1) % externalPolygon.length];

      const intersection = this.findRaySegmentIntersection(prompt.worldPoint, rayDirection, edgeStart, edgeEnd);

      if (intersection && intersection.t > 0) {
        // t > 0 ensures the intersection is in the direction we're shooting
        const distance = prompt.worldPoint.distanceTo(intersection.point);

        if (!bestResult || distance < bestResult.distance) {
          bestResult = {
            edgeIndex: i,
            point: intersection.point.clone(),
            distance,
          };
        }
      }
    }

    return bestResult;
  }

  /**
   * Find intersection between a ray (point + direction) and a line segment.
   * Returns the intersection point if it exists and is on the segment.
   */
  private findRaySegmentIntersection(rayOrigin: THREE.Vector3, rayDirection: THREE.Vector3, segStart: THREE.Vector3, segEnd: THREE.Vector3): { point: THREE.Vector3; t: number } | null {
    // Work in 2D (XY plane)
    const ox = rayOrigin.x,
      oy = rayOrigin.y;
    const dx = rayDirection.x,
      dy = rayDirection.y;
    const sx = segStart.x,
      sy = segStart.y;
    const ex = segEnd.x,
      ey = segEnd.y;

    // Segment direction
    const segDx = ex - sx;
    const segDy = ey - sy;

    const denom = dx * segDy - dy * segDx;

    if (Math.abs(denom) < 0.0001) {
      return null; // Parallel
    }

    // t is parameter along ray, u is parameter along segment
    const t = ((sx - ox) * segDy - (sy - oy) * segDx) / denom;
    const u = ((sx - ox) * dy - (sy - oy) * dx) / denom;

    // Intersection must be on the segment (0 <= u <= 1)
    // Ray can extend in both directions (no constraint on t sign)
    if (u < 0 || u > 1) {
      return null;
    }

    const x = ox + t * dx;
    const y = oy + t * dy;
    // Interpolate Z from segment
    const z = segStart.z + u * (segEnd.z - segStart.z);

    return {
      point: new THREE.Vector3(x, y, z),
      t,
    };
  }

  /**
   * Build a path of intersection points between two external connections,
   * traversing through internal prompt-to-prompt connections.
   */
  private buildPathBetweenExternalConnections(
    conn1: ExternalConnection,
    conn2: ExternalConnection,
    internalConnections: Array<{
      promptA: number;
      promptB: number;
      sideA: 'pos' | 'neg';
      sideB: 'pos' | 'neg';
      point: THREE.Vector3;
    }>,
    prompts: LinePrompt[],
  ): THREE.Vector3[] | null {
    if (internalConnections.length === 0) {
      // No internal connections - direct path if same prompt
      if (conn1.promptIndex === conn2.promptIndex) {
        return [];
      }
      return null;
    }

    // Build adjacency for traversal
    // From each (prompt, side), find the next connection
    const adjacency = new Map<string, { nextPrompt: number; nextSide: 'pos' | 'neg'; point: THREE.Vector3 }>();

    for (const conn of internalConnections) {
      // From promptA's sideA, we reach promptB (entering via sideB, exit via opposite)
      const exitSideB: 'pos' | 'neg' = conn.sideB === 'pos' ? 'neg' : 'pos';
      adjacency.set(`${conn.promptA}-${conn.sideA}`, {
        nextPrompt: conn.promptB,
        nextSide: exitSideB,
        point: conn.point,
      });

      // Reverse direction
      const exitSideA: 'pos' | 'neg' = conn.sideA === 'pos' ? 'neg' : 'pos';
      adjacency.set(`${conn.promptB}-${conn.sideB}`, {
        nextPrompt: conn.promptA,
        nextSide: exitSideA,
        point: conn.point,
      });
    }

    // Start from conn1's prompt, exit via the opposite side of the external connection
    const startPrompt = conn1.promptIndex;
    const startSide: 'pos' | 'neg' = conn1.promptSide === 'pos' ? 'neg' : 'pos';

    const endPrompt = conn2.promptIndex;
    const endSide = conn2.promptSide;

    const path: THREE.Vector3[] = [];
    const visited = new Set<string>();

    let currentPrompt = startPrompt;
    let currentSide = startSide;

    // Traverse until we reach the end prompt's external connection side
    for (let step = 0; step < prompts.length * 2; step++) {
      const key = `${currentPrompt}-${currentSide}`;

      if (visited.has(key)) {
        break; // Loop detected
      }
      visited.add(key);

      // Check if we've reached the destination
      if (currentPrompt === endPrompt && currentSide === endSide) {
        break;
      }

      const next = adjacency.get(key);
      if (!next) {
        break; // Dead end
      }

      path.push(next.point.clone());
      currentPrompt = next.nextPrompt;
      currentSide = next.nextSide;
    }

    return path;
  }

  /**
   * Update prompt connections for visualization when building with external polygon.
   * Sets connections on prompts based on internal and external connections found.
   */
  private updatePromptConnectionsForExternal(
    prompts: LinePrompt[],
    internalConnections: Array<{
      promptA: number;
      promptB: number;
      sideA: 'pos' | 'neg';
      sideB: 'pos' | 'neg';
      point: THREE.Vector3;
    }>,
    externalConnections: ExternalConnection[],
  ): void {
    // Clear existing connections and lengths
    for (const prompt of prompts) {
      prompt.connections = {
        positive: undefined,
        negative: undefined,
      };
      prompt.positiveLength = undefined;
      prompt.negativeLength = undefined;
    }

    // Set internal connections
    for (const conn of internalConnections) {
      const connectionPoint = conn.point.clone();

      const intConnA = prompts[conn.promptA].connections;
      if (intConnA) {
        if (conn.sideA === 'pos') {
          intConnA.positive = {
            targetPromptIndex: conn.promptB,
            targetSide: conn.sideB,
            point: connectionPoint,
          };
        } else {
          intConnA.negative = {
            targetPromptIndex: conn.promptB,
            targetSide: conn.sideB,
            point: connectionPoint,
          };
        }
      }

      const intConnB = prompts[conn.promptB].connections;
      if (intConnB) {
        if (conn.sideB === 'pos') {
          intConnB.positive = {
            targetPromptIndex: conn.promptA,
            targetSide: conn.sideA,
            point: connectionPoint,
          };
        } else {
          intConnB.negative = {
            targetPromptIndex: conn.promptA,
            targetSide: conn.sideA,
            point: connectionPoint,
          };
        }
      }
    }

    // For external connections, we use a special marker (targetPromptIndex = -1)
    // to indicate connection to external polygon
    for (const conn of externalConnections) {
      const extConn = prompts[conn.promptIndex].connections;
      if (extConn) {
        if (conn.promptSide === 'pos') {
          extConn.positive = {
            targetPromptIndex: -1, // Special marker for external
            targetSide: 'pos',
            point: conn.point.clone(),
          };
        } else {
          extConn.negative = {
            targetPromptIndex: -1, // Special marker for external
            targetSide: 'neg',
            point: conn.point.clone(),
          };
        }
      }
    }
  }
}
